// ⚠️ FICHIER ENGENDRÉ — NE PAS ÉDITER À LA MAIN.
// Source : lib/operateur-leviers.js + lib/leviers/*.js
// Régénérer : node scripts/generer-miroir-leviers.js
// Pourquoi ce doublon : CRA/webpack refuse les imports hors de src/ (Jest, lui,
// les accepte — d'où le risque de tests verts sur un build cassé).
// lib/operateur-leviers.js — L'ALLOW-LIST FERMÉE DES LEVIERS ACTIONNABLES.
//
// ══ L'EXIGENCE (Dimitri, 2026-09-08) ════════════════════════════════════════
//
//   « Je veux qu'à terme, l'opérateur puisse actionner chaque bouton de chaque
//     onglet. Tout ce que je peux faire à la main, il doit pouvoir me le
//     proposer — et mon clic l'exécute avec mon identité. C'est ça un bras
//     droit : il ne peut rien faire que je ne puisse faire moi-même, mais il
//     peut tout ce que je peux faire. »
//
//   Et, dans la même respiration : « MAIS PAS D'UN COUP. Chaque action a ses
//     pièges. Les monter d'un coup, c'est monter 175 fois une mécanique jamais
//     éprouvée. Quand la première cassera, je ne saurai pas si les autres ont
//     le même défaut. »
//
// D'où ce fichier : la mécanique se construit UNE fois, les leviers s'ajoutent
// PAR VAGUES, et chaque vague se vérifie avant la suivante. Le champ `vague`
// n'est donc pas décoratif — c'est le rythme que Dimitri a demandé.
//
// ══ L'INVARIANT QUE CE MODULE SERT ══════════════════════════════════════════
//
// L'opérateur ne détient JAMAIS de moyen d'agir. Il compose une INTENTION
// (nom de levier + arguments). C'est le NAVIGATEUR de Dimitri, avec SON jeton,
// qui appelle l'action existante. L'acteur audité est donc Dimitri.
//
// ⚠️ CE MODULE NE SAIT PAS AGIR, ET C'EST VOULU. Il ne contient aucun appel
// HTTP, aucun client Stripe, aucun client Supabase — seulement des
// DESCRIPTIONS. Une sentinelle vérifie qu'aucun `lib/operateur-*.js` n'importe
// un module de levier : c'est ce qui ferme le raccourci « le serveur exécute
// à la place du navigateur », par lequel l'invariant fuirait sans bruit.
//
// ══ LES EXCLUSIONS — CE QUI N'ENTRE PAS ═════════════════════════════════════
//
// E1 — rien qui détruit sans retour (DELETE dur, Storage, argent, envoi à un
//      tiers, push OTA non rejouable). Une suppression LOGIQUE réversible
//      (deleted_at, tombstone) n'est pas concernée.
// E2 — rien dont l'effet n'est pas constatable à l'écran juste après.
// E3 — rien qui écrit dans une colonne qu'un AUTRE écrivain complète.
//      ⚠️ Née de la fermeture de #1327 : un cron y recopiait `caution_status`
//      sans faire la propagation vers `direct_booking_requests` que le webhook
//      fait aussi. E3 interdit d'écrire la colonne EN DOUBLE — appeler
//      l'action officielle qui fait tout le travail est le REMÈDE, pas
//      l'infraction. Ne jamais inverser ce critère.
// E4 — rien qui demande à Dimitri d'ATTESTER un fait. `capture_caution` exige
//      `voyageur_notified: true` : une intention composée par l'opérateur
//      affirmerait À SA PLACE que le voyageur a été prévenu.
// E5 — rien qui laisse l'opérateur agir SUR LUI-MÊME (se relancer, régler sa
//      propre autonomie, refuser ses propres propositions, ouvrir sa vanne
//      d'envoi réel). Techniquement propre, politiquement non.
//      ⚠️ EN ATTENTE D'ARBITRAGE DIMITRI au 2026-09-08 — appliquée par
//      précaution en attendant, jamais relâchée sans son mot.
//
// ── ET CE QUI N'EST PAS UNE EXCLUSION : LE TEXTE LIBRE ──────────────────────
// Une action qui demande un texte que Dimitri doit écrire n'est PAS écartée :
// elle est mise en DERNIÈRE VAGUE (`vague: 9`). Décision Dimitri : « le jour
// où il saura proposer un brouillon que je réécris, ça servira. »
//
// ══ COMMENT ON AJOUTE UN LEVIER ═════════════════════════════════════════════
// 1. Vérifier les cinq exclusions SUR LE CODE du handler, pas sur son nom.
// 2. Lui donner une vague. Ne jamais l'ajouter à une vague déjà vérifiée.
// 3. `args_autorises` est une liste FERMÉE : tout argument hors liste est
//    refusé côté serveur ET côté client. C'est ce qui garantit que l'opérateur
//    ne CHOISIT jamais — jamais un montant, jamais un prix, jamais un texte
//    destiné à un tiers ; seulement des identifiants et des dates observées.
// 4. `constatable` dit CE QUE Dimitri verra après le clic. S'il n'y a rien à
//    écrire ici, le levier ne passe pas E2.

// ══ LES INTERDITS ABSOLUS — ET POURQUOI, CHACUN ════════════════════════════
//
// Ces leviers ne sont pas « en attente d'une vague ». Ils n'entreront JAMAIS,
// et la raison voyage avec la règle : une interdiction sans son motif finit
// par être levée par quelqu'un qui la croit prudente à l'excès.
//
// Décisions Dimitri du 2026-09-08, ses mots gardés. Sa gradation générale est
// « une sécurité proportionnelle au risque, pas une interdiction » — ce qui
// suit est la liste des cas où AUCUNE confirmation ne suffit, parce que le
// dommage n'est pas réparable PAR LUI.
// ⚠️ RECLASSEMENT DU 2026-09-14 — TROIS SONT SORTIS D'ICI, ET C'EST LA RÈGLE
// QUI L'EXIGE. `capture_caution`, `resolve_caution` et
// `beds24_reconciliation_resolve` figuraient dans cette liste ; Dimitri les a
// rouverts le 14/09 sous trois gardes. Or sa règle de la veille dit :
// « un interdit absolu ne revient jamais ; un écarté peut revenir — à
// condition de le dire. » Un interdit qu'on rouvre vide la liste de son sens
// et efface la frontière avec les écartés. Ils sont donc passés dans ECARTES,
// avec leur motif ET les gardes qui les autorisent à revenir — voir la section
// « rouverts le 2026-09-14 » plus bas. Ce qui reste ici ne se rouvre pas.
//
// ⚠️ ET LEURS VOISINS RESTENT : `refund_caution_partial` (attestation, E4) et
// `booking_cancel_paid` (trois irréversibles en une orchestration) n'ont PAS
// été rouverts. La proximité de famille n'est pas une décision.
// ⚠️ Le miroir `src/lib/` a son propre `accord-nombres.js` au même niveau
// relatif : ce `require` reste valide des deux côtés.
const { accord, nomAccorde } = require("./accord-nombres.js");

const INTERDITS_ABSOLUS = Object.freeze({
  // ── L'argent qui part ────────────────────────────────────────────────────
  refund_caution_partial:
    "Remboursement définitif, et l'action exige d'ATTESTER que le voyageur a été prévenu — personne ne peut taper cela à la place de Dimitri.",
  booking_cancel_paid:
    "Remboursement + annulation de caution + réouverture Beds24, en une orchestration. Trois irréversibles d'un coup.",

  // ── Le dommage chez un tiers ─────────────────────────────────────────────
  // ⚠️ IL RESTE INTERDIT, ET UNE AUTRE PORTE S'EST OUVERTE À CÔTÉ — les deux
  // sont vraies, et la raison doit dire les deux, sinon quelqu'un lèvera
  // l'interdit en croyant réparer une incohérence.
  pricing_block_release:
    "Rouvre la vente d'une date bloquée à partir d'un simple identifiant, SANS aucune vérification : ni confirmation, ni relecture des réservations, ni contrôle chez Beds24. Le double-booking se produit chez un tiers, pas chez nous. ⚠️ Remettre des nuits en vente EXISTE pourtant pour l'opérateur depuis le 2026-09-17 : c'est `pricing_block_reopen`, une AUTRE action, qui retrouve le blocage par la PÉRIODE et porte les trois gardes d'ADR-256/257. Celle-ci, la porte sans serrure, ne se rouvre pas.",

  // ── Ce que la loi fige ───────────────────────────────────────────────────
  // ⚠️ `setInvoiceDelivered` (ÉMETTRE une facture neuve) A QUITTÉ CETTE LISTE
  // LE 2026-10-01, décision Dimitri : « émettre une facture neuve = validation
  // à la main ». Même règle que le 2026-09-14 : un interdit absolu qui se
  // rouvre n'en était pas un — il est DESCENDU dans ECARTES, sous gardes (voir
  // « rouvert le 2026-10-01 »). Ses voisins ci-dessous RESTENT : ils touchent
  // une facture DÉJÀ émise (la supprimer, l'annuler par un avoir, caler la
  // série), et la décision du même jour les maintient interdits.
  deleteInvoiceHard:
    "Supprimer une pièce comptable, y compris déjà transmise. Dimitri : « modifier une facture envoyée, c'est faux au regard de la loi. Un avoir, jamais une réécriture. »",
  emitCreditNote:
    "Émet un AVOIR : une pièce comptable neuve, numérotée dans la série AV-*, qui annule une facture émise. C'est le geste légal (le seul) pour défaire une facture — et précisément parce qu'il est légal, il est irréversible : un avoir émis le reste. Dimitri le fait lui-même, depuis l'Historique.",
  // ⚠️ AJOUT DU 2026-09-15, décision Dimitri : « Poser le dernier numéro d'une
  // série : interdit absolu. Confirmé. » Ses deux voisins y étaient déjà ; un
  // compteur qu'on cale à la main est la troisième face du même dommage.
  setInvoiceSequence:
    "Cale le dernier numéro d'une série de factures (CGI 289 : numérotation continue). Un compteur posé trop bas fabrique un DOUBLON à la prochaine émission, trop haut un TROU dans la série — deux irrégularités qu'aucun clic ne répare. Dimitri : « interdit absolu. Confirmé. »",

  // ── Ce qui emporte plus que son nom n'annonce ────────────────────────────
  // Dimitri, 2026-09-08 : « la suppression d'un logement efface plus qu'elle
  // n'annonce — un mot à taper ne dit pas qu'on perd toutes les réservations
  // avec ». La règle est plus large que l'action : un geste dont l'effet
  // DÉBORDE ce que son libellé décrit ne peut pas être approuvé d'un clic,
  // parce que Dimitri approuverait ce qu'il a lu, pas ce qui se produirait.
  logement_delete:
    "Efface en cascade tout ce qui pend au logement — réservations comprises. Le libellé n'annonce pas l'étendue ; un clic ne peut pas valoir consentement à ce qu'il ne dit pas.",

  // ── Ce que Dimitri fait lui-même ─────────────────────────────────────────
  //
  // ⚠️ DEUX AJOUTS DU 2026-09-09, ses mots, en préparant la vague 4.
  pricing_force_resync:
    "Écrase les prix fixés à la main sur toute la période lue chez Beds24, et le prix d'origine n'est nulle part — le trigger ne neutralise QUE le cron et le prix de base, jamais cette source. Dimitri : « un geste irréversible qui écrase mes prix, je le ferai à la main en sachant ce que je fais. »",
  inbox_ia_config_save:
    "Bascule l'éligibilité d'un logement au copilote — l'un des DEUX verrous du chantier IA. Dimitri : « c'est un de mes deux verrous, je ne veux pas qu'il puisse le lever. » C'est E5 dans sa forme la plus nette : l'opérateur ne desserre pas ce qui le tient.",
  purge_id_documents:
    "Purge des pièces d'identité : légale et irréversible. Dimitri : « je la ferai moi-même ». L'opérateur peut la SIGNALER, jamais la proposer.",
});

// ══ LES ÉCARTÉS — CE QUI N'ENTRE PAS AUJOURD'HUI, ET POURQUOI, CHACUN ═══════
//
// ⚠️ CE N'EST PAS LA MÊME LISTE QUE CI-DESSUS, ET LA DIFFÉRENCE EST TOUTE LA
// RÈGLE. Un INTERDIT ABSOLU ne doit JAMAIS exister : le dommage n'est pas
// réparable par Dimitri. Un ÉCARTÉ POURRAIT exister un jour, si Dimitri le
// décide — il a été tenu dehors sur une exclusion (E1→E5) lue dans le CODE
// du handler, à une date donnée, et cette raison peut être ré-examinée.
//
// Dimitri, 2026-09-14 : « grave-les comme les interdits absolus. Chacun avec
// sa raison, et un test qui fait échouer le chargement si l'un revient. »
// Jusque-là, les 41 écartés du tri (#1371, #1378) ne vivaient que dans des
// commentaires et des messages de commit : un levier écarté pouvait revenir
// au registre sans que rien ne lève. Un écarté sans raison écrite est un
// écarté qu'on ré-ouvrira ; un écarté sans garde est un écarté qui revient.
//
// ⚠️ COMMENT ON EN SORT UN, PROPREMENT. Si Dimitri décide qu'un écarté entre :
//   1. le retirer d'ICI (jamais assouplir `_agreger`) ;
//   2. DIRE POURQUOI — dans le commit ET dans le commentaire du levier déclaré,
//      en citant ce qui a changé (une garde serveur ajoutée, une décision de
//      Dimitri, un motif devenu faux) ;
//   3. ajuster le compte dans `src/lib/operateur-leviers-ecartes.test.js`.
// Une protection qu'on ne peut pas lever proprement finit contournée
// salement ; celle-ci se lève en trois gestes, tous visibles en revue.
//
// Chaque entrée : `depuis` (date du tri), `exclusion` (E1→E5, ou `hors_atteinte`
// quand le clic de Dimitri ne PEUT pas l'exécuter, ou `pii`), `raison` (le
// résumé du raisonnement — le détail vit dans le fichier de famille cité).
// Les raisons sont RETROUVÉES dans `lib/leviers/*.js`, pas inventées ici.
const ECARTES = Object.freeze({
  // ── E5 — les onze gestes de sa propre mécanique (lib/leviers/operateur.js) ─
  // Balayés le 2026-09-12 : handlers propres ; ce n'est pas une question de
  // risque, c'est une question de QUI DÉCIDE. Un bras droit qui compose
  // l'intention de lever ses propres freins n'est plus tenu par rien.
  operateur_etat_toggle: {
    depuis: "2026-09-12",
    exclusion: "E5",
    raison:
      "LE coupe-circuit (`en_marche`). Se relancer lui-même est le cas d'école d'E5 ; à l'arrêt, `claim` refuse déjà tout (409) — ce levier rouvrirait la vanne qui le retient.",
  },
  operateur_autonomie_set: {
    depuis: "2026-09-12",
    exclusion: "E5",
    raison:
      "Règle le curseur demande/propose/fait_seul de ses propres actes. E5 mot pour mot : « régler sa propre autonomie ».",
  },
  operateur_proposition_claim: {
    depuis: "2026-09-12",
    exclusion: "E5",
    raison:
      "Décider du sort de SES propositions. E5 : « refuser ses propres propositions » — réclamer en est le jumeau. L'opérateur n'agit pas sur sa propre file.",
  },
  operateur_proposition_confirm: {
    depuis: "2026-09-12",
    exclusion: "E5",
    raison:
      "`confirm` est la TRACE posée APRÈS que le navigateur a appelé le levier. Composer « confirme X » passerait une ligne en `acceptee` sans que le geste ait eu lieu — le journal dirait « parti » sur un envoi qui n'a jamais existé.",
  },
  operateur_proposition_refuse: {
    depuis: "2026-09-12",
    exclusion: "E5",
    raison:
      "E5 mot pour mot : « refuser ses propres propositions ». Un clic dont Dimitri croirait qu'il porte sur autre chose.",
  },
  operateur_regle_toujours: {
    depuis: "2026-09-12",
    exclusion: "E5",
    raison:
      "Écrit une règle permanente dans les instructions IA de chaque logement éligible (ou en mémoire) : il se réécrirait sa propre consigne de fond.",
  },
  operateur_memoire_decide: {
    depuis: "2026-09-12",
    exclusion: "E5",
    raison:
      "Garder / verrouiller / oublier / TOUT oublier ses propres règles retenues. `tout_oublier` tombale toutes les règles vivantes d'un appel : l'effet déborde le libellé, et s'ajoute à E5.",
  },
  operateur_plan_toggle: {
    depuis: "2026-09-12",
    exclusion: "E5",
    raison:
      "Allumer ou éteindre ses propres passages planifiés : se donner du travail récurrent, ou s'en retirer, sans que personne d'autre ne l'ait voulu.",
  },
  operateur_plan_decide: {
    depuis: "2026-09-12",
    exclusion: "E5",
    raison:
      "Accepter ou refuser ses propres passages planifiés — même motif que `operateur_plan_toggle`.",
  },
  operateur_acte_defaire: {
    depuis: "2026-09-12",
    exclusion: "E5",
    raison:
      "Défaire un de SES actes déjà posés. Le geste va dans le bon sens, mais le « ↩ Défaire » est la main de Dimitri sur ce qu'il relit, jamais une proposition de l'intéressé.",
  },
  operateur_conversation: {
    depuis: "2026-09-12",
    exclusion: "E2",
    raison:
      "C'est LUI. L'action n'écrit rien (aucune table, aucun état) : rien à constater, E2 tombe avant E5 ; un levier qui pointe l'endpoint qui l'a composé est une boucle, pas un geste.",
  },
  inbox_real_send_config_save: {
    depuis: "2026-09-09",
    exclusion: "E5",
    raison:
      "`real_send_enabled` est la 2e barrière du copilote : la lever, c'est l'opérateur qui propose de retirer ce qui le tient (« ouvrir sa vanne d'envoi réel »). Refermer serait inoffensif, mais `args_autorises` filtre des clés, jamais des valeurs : ouvrir et fermer voyagent ensemble (lib/leviers/messages.js).",
  },

  // ── E4 — l'attestation ───────────────────────────────────────────────────
  menage_run_complete: {
    depuis: "2026-09-12",
    exclusion: "E4",
    raison:
      "Passer un run en `completed` ATTESTE deux faits non observés : que le ménage a eu lieu, et qu'il est dû (« ménage fait = dû au prestataire », art. 7.2 du contrat). Et l'ADMIN est exempté de la garde photo : la seule protection sauterait précisément sur le chemin ouvert (lib/leviers/menage.js).",
  },
  livret_module_unlock: {
    depuis: "2026-09-12",
    exclusion: "E4",
    raison:
      "Action ANON exécutée par LE VOYAGEUR (4 derniers chiffres de son téléphone = preuve d'identité, ADR-172). Composer ces chiffres, c'est affirmer une identité à sa place ; chaque essai raté consomme le lockout du vrai voyageur (lib/leviers/livret.js).",
  },
  taxe_bareme_validate: {
    depuis: "2026-09-09",
    exclusion: "E4",
    raison:
      "Le handler écrit `statut:'valide'` + `validated_by = Dimitri` : le clic ATTESTE qu'un taux légal a été vérifié à la source officielle. Un taux composé de mémoire serait signé de son nom. Rentrerait en vague 9 avec le taux de `taxe_bareme_detect`, jamais un chiffre inventé (lib/leviers/pricing.js).",
  },
  controle_create: {
    depuis: "2026-09-09",
    exclusion: "E4",
    raison:
      "Le verdict et `points[].texte` sont une OBSERVATION DE TERRAIN. L'opérateur n'a jamais mis les pieds sur place : composer ce texte serait fabriquer un constat (lib/leviers/menage.js).",
  },

  // ── E1 — ce qui ne revient pas ───────────────────────────────────────────
  contracts_orphans_delete: {
    depuis: "2026-09-12",
    exclusion: "E1",
    raison:
      "`storage.remove` sur les PDF de CONTRATS SIGNÉS d'une demande disparue. Un PDF effacé ne se régénère pas, il faudrait refaire signer ; et ce que Dimitri verrait après, c'est un compteur, jamais ce qu'il a perdu (lib/leviers/voyageurs.js).",
  },
  livret_module_photo_delete: {
    depuis: "2026-09-09",
    exclusion: "E1",
    raison:
      "Ce qui part est un FICHIER du bucket `livret-photos`, pas un texte retapable. Tension assumée et REMONTÉE avec `logement_gallery_delete` (déclaré) : arbitrage Dimitri, qui vaut pour les deux d'un coup (lib/leviers/livret.js).",
  },
  livret_photo_delete: {
    depuis: "2026-09-12",
    exclusion: "E1",
    raison:
      "Son seul argument est un `storage_path` BRUT : aucune ligne lue, aucune appartenance vérifiée, `storage.remove([path])` et rien d'autre. Rien à constater (la ligne reste, l'image casse — E2) et rien ne borne la cible : le chemin vient du modèle (lib/leviers/livret.js).",
  },
  livret_sections_auto_delete: {
    depuis: "2026-09-09",
    exclusion: "E1",
    raison:
      "DELETE SQL de toute la section : régénérer ne restaure pas les réglages manuels perdus, ce n'est pas une suppression logique (lib/leviers/livret.js).",
  },
  signalement_voyageur_supprimer: {
    depuis: "2026-09-09",
    exclusion: "E1",
    raison:
      "`.delete()` SEC sur la table puis `storage.remove(paths)` sur les photos, best-effort assumé (« on ne ressuscite rien sur un échec Storage »). E1 nomme DELETE dur et Storage : les deux sont là (lib/leviers/voyageurs.js).",
  },
  menage_instruction_delete: {
    depuis: "2026-09-09",
    exclusion: "E1",
    raison:
      "DELETE dur sans tombstone (« la ligne disparaît, elle n'est pas archivée »). Le motif RGPD qui justifie l'action exige que Dimitri CONSTATE lui-même le contenu problématique avant d'effacer ; `menage_instruction_archive`, réversible, couvre le rangement (lib/leviers/menage.js).",
  },
  inbox_ia_faq_delete: {
    depuis: "2026-09-09",
    exclusion: "E1",
    raison:
      "DELETE dur, rien ne revient — et sa version RÉVERSIBLE est déclarée : `inbox_ia_faq_update` avec `status:'archived'` (lib/leviers/messages.js).",
  },
  shared_fragments_delete: {
    depuis: "2026-09-09",
    exclusion: "E1",
    raison:
      "DELETE dur ; la voie réversible `shared_fragments_save` `is_active:false` est déclarée. Et un corps qui cite encore `{{frag:KEY}}` ne casse pas là où on supprime mais AILLEURS, au prochain envoi : l'effet déborde le libellé (lib/leviers/messages.js).",
  },
  direct_messages_delete: {
    depuis: "2026-09-09",
    exclusion: "E1",
    raison:
      "DELETE dur ; la version réversible `direct_messages_settings` `status:'brouillon'` est déclarée. Le vrai effacement reste un geste de la main, devant le texte qu'on perd (lib/leviers/messages.js).",
  },
  pricing_block_edit_dates: {
    depuis: "2026-09-09",
    exclusion: "E1",
    raison:
      "`pushBlockAvailability` fait un push DIFFÉRENTIEL : toute réduction de plage renvoie `numAvail=1` sur les dates libérées. C'est `pricing_block_release` (interdit absolu) par un autre chemin — interdire la porte et laisser la fenêtre (lib/leviers/pricing.js). ⚠️ La fenêtre reste fermée après l'ouverture de `pricing_block_reopen` (2026-09-17) : celui-ci REFUSE de libérer un morceau de blocage (409), précisément pour ne pas devenir ce geste-ci par la bande.",
  },
  services_orchestrateur_grant: {
    depuis: "2026-09-09",
    exclusion: "E1",
    raison:
      "CAPTURE PARTIELLE Stripe réelle : l'argent bouge vraiment, immédiatement. Même famille que `capture_caution` — « un mot tapé protège d'un faux clic, pas d'une prémisse fausse » (lib/leviers/logements.js).",
  },
  services_orchestrateur_hourly_refuse: {
    depuis: "2026-09-09",
    exclusion: "E1",
    raison:
      "RELEASE TOTAL du hold Stripe — on ne peut plus reprendre une autorisation relâchée. Vrai mouvement de fonds, jamais un candidat au clique-pour-proposer (lib/leviers/logements.js).",
  },

  // ── E2 — rien à constater juste après ────────────────────────────────────
  livret_sections_auto_photo_upload_url: {
    depuis: "2026-09-12",
    exclusion: "E2",
    raison:
      "Rend une URL d'upload SIGNÉE, première moitié d'un geste dont la seconde est un PUT binaire. L'opérateur ne détient aucun fichier : une URL que personne n'utilise, et un `constatable` qui serait « rien ne change » (lib/leviers/livret.js).",
  },
  logement_gallery_upsert: {
    depuis: "2026-09-09",
    exclusion: "E2",
    raison:
      "La création suppose une photo déjà en Storage (signed URL + PUT faits par le NAVIGATEUR) ; composer l'intention ferait accepter un `public_url` https ARBITRAIRE (le handler ne vérifie que la forme). On ne sait pas dire à Dimitri ce qui apparaîtra. Raison réécrite le 2026-09-12 : l'argument « pas de verbe HTTP » est mort (`methode` existe) ; un levier PATCH « renommer » reste à écrire (lib/leviers/logements.js).",
  },
  pricing_logement_config: {
    depuis: "2026-09-09",
    exclusion: "E2",
    raison:
      "Raison réécrite le 2026-09-12 (« pas de verbe » est mort). Ce qui reste : QUATRE faces pour une action (GET/POST+seed/PATCH/DELETE) dont deux hors d'atteinte de `methode` ; la bascule `manual ↔ pricelabs` se joue chez PriceLabs/Beds24, non constatable le jour même ; et le verrou `If-Match` absent d'une intention composée ferait écraser en silence ce qu'un autre admin vient de régler (lib/leviers/pricing.js).",
  },

  // ── E3 — écrivain partiel d'une colonne qu'un autre complète ─────────────
  reservation_set_caution_status: {
    depuis: "2026-09-09",
    exclusion: "E3",
    raison:
      "N'écrit AUCUN statut : pose `caution_received_at` seule, là où le webhook Stripe écrit la colonne GROUPÉE (+ status, intent, session) puis propage vers `direct_booking_requests` — #1327 avec un bouton. Et poser ce timestamp DÉCLENCHE un envoi voyageur (`event_caution_ok`) que la carte ne dirait pas (lib/leviers/voyageurs.js).",
  },
  reservation_set_payment_status: {
    depuis: "2026-09-09",
    exclusion: "E3",
    raison:
      "Même défaut : pose `payment_received_at` seule, là où `applyPaiementSejourStatus` (webhook) écrit statut + `paid_at` + intent + propagation. L'écrivain complet est le webhook, qui n'est pas une action routée : rien à substituer ⇒ rien à déclarer (lib/leviers/voyageurs.js).",
  },
  livret_sections_auto_upsert: {
    depuis: "2026-09-09",
    exclusion: "E3",
    raison:
      "Écrit `content` SEUL là où `livret_sections_auto_generate` écrit la même ligne avec `enabled` ET `last_generated_at` : écrivain PARTIEL (le sosie de #1327). Après un upsert, l'écran affiche la date de génération d'AVANT sur un contenu qui a changé (lib/leviers/livret.js).",
  },
  pricing_block_update_internal: {
    depuis: "2026-09-09",
    exclusion: "E3",
    raison:
      "Raison réécrite le 2026-09-12 (« pas d'objet » est mort). Ce qui reste : `validateGuestFields` est UNE liste de 13 clés dont `total_amount` — une sous-liste au registre serait une garde MAISON sur le chemin argent ; l'identité réécrite est déjà figée dans un contrat SIGNÉ ; et poser un champ voyageur sort le bloc de l'auto-résolution Beds24 sans que la carte le dise. Doute remonté : rentrerait avec une `args_formes` sans `total_amount`/`guest_email`/`guest_phone` — arbitrage chemin argent, donc celui de Dimitri (lib/leviers/pricing.js).",
  },

  // ── Hors d'atteinte du clic de Dimitri ───────────────────────────────────
  pricing_sync_fetch_beds24: {
    depuis: "2026-09-09",
    exclusion: "hors_atteinte",
    raison:
      "`CRON_ACTIONS`, authentifié Bearer `CRON_SECRET`. Le navigateur de Dimitri n'a que son JWT admin : la carte s'afficherait et le clic répondrait 401 — un bouton mort qu'il découvrirait lui-même (lib/leviers/pricing.js).",
  },
  beds24_reconciliation_auto_resolve: {
    depuis: "2026-09-09",
    exclusion: "hors_atteinte",
    raison:
      "Même cas : action de cron sous `CRON_SECRET`, inexécutable avec le jeton de Dimitri (lib/leviers/pricing.js).",
  },
  controle_photo_upload: {
    depuis: "2026-09-09",
    exclusion: "hors_atteinte",
    raison:
      "La preuve est une PHOTO — un binaire que l'opérateur ne peut pas produire, pièce jointe d'un constat de terrain qu'il n'a pas fait (lib/leviers/menage.js).",
  },
  controle_agent_photo_reprise: {
    depuis: "2026-09-09",
    exclusion: "hors_atteinte",
    raison:
      "Même motif que `controle_photo_upload` : une photo de reprise, binaire, prise sur place par l'agent (lib/leviers/menage.js).",
  },
  controle_agent_point_fait: {
    depuis: "2026-09-09",
    exclusion: "hors_atteinte",
    raison:
      "Ne s'exécute QUE si `run.assigned_provider_email === user.email` : cliquée avec le jeton de Dimitri, 404 systématique. Elle exige l'IDENTITÉ de l'agent assigné — ce qui contredit l'invariant même du bras droit (lib/leviers/menage.js).",
  },
  // ⚠️ FERMÉ LE 2026-09-22, ET IL ÉTAIT OUVERT EN VAGUE 1 — c'est ce qui en
  // fait le seul de cette section à avoir MENTI en production. Les autres
  // « hors d'atteinte » sont des gestes qu'on n'a jamais proposés ; celui-ci
  // occupait une ligne du catalogue, le modèle le proposait, et la carte ne
  // venait jamais. Il ne se rouvre QUE derrière un repère qui désigne une
  // photo (le rang, une fois une galerie rangée) — pas avant.
  logement_gallery_set_cover: {
    depuis: "2026-09-22",
    exclusion: "hors_atteinte",
    raison:
      "Exige `id`, l'identifiant d'une ligne de galerie, que RIEN ne rend : `logement_gallery_lire` sert des rangs et un drapeau de couverture, jamais un identifiant, et les sept photos de lc1 sont toutes à `order_index = 0` — donc même le rang se refuse. Ouvert en vague 1, il occupait une ligne du catalogue pour un geste qui ne pouvait jamais s'armer. Revient avec son repère, pas avant (lib/leviers/logements.js).",
  },

  // ── RÉORDONNER — LES CINQ, FERMÉS LE 2026-09-22 (décision D3) ────────────
  //
  // ⚠️ ILS SONT CINQ, PAS QUATRE, ET LE COMPTE COMMANDE LA DÉCISION. Le
  // cadrage n'en voyait que quatre : `direct_messages_reorder` serait resté
  // OUVERT (vague 2) et inarmable, c'est-à-dire exactement le défaut qu'on
  // ferme. Ils partent donc ensemble.
  //
  // Le motif commun, en une phrase : leur argument est une LISTE ENTIÈRE
  // d'identifiants dans l'ordre voulu, et aucun repère parlé ne produit une
  // liste entière. Dimitri dit « fais remonter le wifi en premier » ; le lot
  // 2a, qui apprend à désigner un objet PAR SON NOM, rend UN objet — jamais un
  // classement. Et même un « mets-le en premier » n'aurait pas d'ordre de
  // départ : `ordreEnregistre()` rend FAUX sur ce parc, les treize modules du
  // livret sont tous à `order_index = 0`.
  //
  // ⚠️ CE QUI LES RAMÈNERA : un repère de DÉPLACEMENT RELATIF (« mets-le avant
  // celui-ci »), et un parc où quelque chose a été rangé. C'est un lot à part.
  direct_messages_reorder: {
    depuis: "2026-09-22",
    exclusion: "hors_atteinte",
    raison:
      "Exige `ordered_slugs` : la liste ENTIÈRE des messages d'une catégorie, dans l'ordre voulu. Aucun repère parlé ne produit une liste entière — Dimitri nomme UN message, jamais huit slugs à la suite. Ouvert en vague 2, il occupait une ligne du catalogue pour un geste qui ne pouvait jamais s'armer (lib/leviers/messages.js).",
  },
  livret_module_reorder: {
    depuis: "2026-09-22",
    exclusion: "hors_atteinte",
    raison:
      "Exige `ordered_ids`, la liste entière des modules d'une catégorie. Et le parc n'a AUCUN ordre enregistré (`ordreEnregistre()` rend faux : les treize modules sont à `order_index = 0`), donc même un « mets-le en premier » n'aurait pas de point de départ (lib/leviers/livret.js).",
  },
  livret_category_reorder: {
    depuis: "2026-09-22",
    exclusion: "hors_atteinte",
    raison:
      "Même motif : `ordered_ids` est la liste entière des catégories d'un livret, et un repère parlé ne désigne qu'un objet à la fois. Le déplacement relatif est un lot à part (lib/leviers/livret.js).",
  },
  livret_assistance_contact_reorder: {
    depuis: "2026-09-22",
    exclusion: "hors_atteinte",
    raison:
      "Même motif, et deux fois plutôt qu'une : `ordered_ids` est la liste entière, et la section assistance n'existe QUE sur lc1 — dix logements sur onze n'ont aucune ligne `livret_sections_auto` (lib/leviers/livret.js).",
  },
  livret_alentours_poi_reorder: {
    depuis: "2026-09-22",
    exclusion: "hors_atteinte",
    raison:
      "Même motif : `ordered_place_ids` est la liste entière des lieux d'un secteur, dans l'ordre voulu, et aucune phrase de Dimitri ne la produit (lib/leviers/livret.js).",
  },

  // ── PII — la nature de l'action, pas sa qualité ──────────────────────────
  orchestrateur_guest_contact: {
    depuis: "2026-09-09",
    exclusion: "pii",
    raison:
      "Sert le NOM et le TÉLÉPHONE du voyageur : rien à constater hormis la donnée qui vient de sortir, et l'opérateur n'a aucune raison de PROPOSER cette divulgation — Dimitri clique le bouton existant lui-même, en connaissance de cause à cet instant (lib/leviers/menage.js).",
  },

  // ── ROUVERTS LE 2026-09-14 — descendus des INTERDITS ABSOLUS ─────────────
  //
  // Ces trois-là étaient des interdits absolus. Dimitri les a rouverts le
  // 14/09, sous trois gardes qu'il a nommées lui-même. La règle de la veille
  // impose alors de les DESCENDRE ici plutôt que de les laisser là-haut : un
  // interdit absolu qui se rouvre n'est plus un interdit absolu, et la liste
  // cesse de vouloir dire quelque chose pour tous les autres.
  //
  // ⚠️ ÊTRE ÉCARTÉ N'EST PAS ÊTRE OUVERT. Aucun des trois n'est composable
  // aujourd'hui : il n'existe pas de levier. `gardes` dit à quelles conditions
  // on aurait le droit d'en écrire un — ce n'est pas une autorisation, c'est
  // le cahier des charges de celui qui le fera.
  capture_caution: {
    depuis: "2026-09-14",
    exclusion: "argent_sous_gardes",
    raison:
      "L'argent quitte la carte du voyageur — motif d'origine intact : « un mot tapé protège d'un faux clic, pas d'une prémisse fausse ». Rouvert par Dimitri le 2026-09-14, jamais à la voix, jamais sans les trois gardes ci-dessous (action de api/stripe.js, famille lib/leviers/logements.js).",
    gardes: [
      "l'opérateur ne PROPOSE jamais le montant — Dimitri le dit, l'opérateur le reprend tel quel",
      "tout le contexte AVANT : qui, combien, ce qui le justifie, et ce que ces dates portaient",
      "confirmation du NOM, pas d'un clic — se tromper de personne est le vrai risque, et un clic ne l'attrape pas",
    ],
  },
  resolve_caution: {
    depuis: "2026-09-14",
    exclusion: "argent_sous_gardes",
    raison:
      "Libération seule : annulation définitive du hold, irréversible. Le mode capture est refusé depuis le 2026-10-07 (Argent 1a) — encaisser passe uniquement par `capture_caution`. Rouvert le 2026-09-14 sous les mêmes trois gardes que `capture_caution` (action de api/stripe.js, famille lib/leviers/logements.js).",
    gardes: [
      "l'opérateur ne PROPOSE jamais le montant ni le mode",
      "tout le contexte AVANT : qui, combien, ce qui le justifie",
      "confirmation du NOM du voyageur, jamais un clic seul",
    ],
  },
  beds24_reconciliation_resolve: {
    depuis: "2026-09-14",
    exclusion: "tiers_sous_gardes",
    raison:
      "Le mode `rouvrir` pousse numAvail=1 : une OTA peut vendre dans les minutes qui suivent, et un voyageur arrive devant une porte déjà prise. Le dommage se produit chez un tiers. Rouvert le 2026-09-14 — et la triple garde fail-closed d'ADR-256/257 est la condition, pas un supplément (lib/leviers/pricing.js). ⚠️ Ces trois gardes vivent depuis le 2026-09-17 dans `lib/beds24-reopen-guards.js`, partagées avec `pricing_block_reopen` et le bouton « Débloquer » de la fiche : une seule vérité, trois portes.",
    gardes: [
      "confirmation explicite de Dimitri, à la main",
      "re-vérification de la réservation locale au moment du geste",
      "cross-check Beds24 LIVE par CHEVAUCHEMENT de dates — jamais une fenêtre bornée des deux côtés (ADR-257)",
    ],
  },

  // ── ROUVERT LE 2026-10-01 — descendu des INTERDITS ABSOLUS ───────────────
  //
  // Dimitri, 2026-10-01 : « émettre une facture neuve = validation à la main ».
  // Ce n'est plus « jamais », c'est « jamais à la voix seule » : la voix peut
  // préparer la carte ou l'emmener vers Argent › Historique, elle n'exécute
  // pas. Toucher une facture DÉJÀ émise (supprimer, avoir, série) reste
  // interdit — ce sont les voisins restés là-haut.
  //
  // ⚠️ ÉCARTÉ N'EST PAS OUVERT : il n'existe AUCUN levier d'émission. La
  // « validation à la main » existe déjà comme mécanisme au registre —
  // `confirmation_au_clic` (case cochée sur la carte, jamais un argument,
  // jamais proposée, refusée à la voix), celui de « Remettre en vente ». Ce
  // qui manque est ailleurs : la carte ne sait ni désigner ni lire un
  // BROUILLON (`lib/facture-repere.js` et la lecture `facture_lire` refusent
  // toute pièce sans numéro), et le serveur n'exige aucune confirmation sur
  // cette action. Les gardes ci-dessous sont le cahier des charges du levier.
  setInvoiceDelivered: {
    depuis: "2026-10-01",
    exclusion: "argent_sous_gardes",
    raison:
      "Émettre une facture neuve attribue le numéro officiel : la séquence est légalement continue (CGI 289), un numéro attribué le reste, seul un avoir défait la pièce. Rouvert par Dimitri le 2026-10-01 en validation À LA MAIN, jamais à la voix — aucun levier n'existe encore (action de lib/invoice-handlers.js, famille lib/leviers/argent.js).",
    gardes: [
      "validation à la main sur la carte (`confirmation_au_clic`) : jamais proposée, jamais lancée par un « oui » entendu",
      "l'opérateur ne désigne qu'un BROUILLON existant, lu et nommé avant le clic (mois, propriétaire, montant) — jamais un montant, jamais un numéro",
      "`delivered: true` imposé par le registre, jamais composé : la même action retire aussi « envoyé », et ce second geste n'a rien à faire sur cette carte",
      "une confirmation exigée AUSSI côté serveur (comme `confirm_required` de la réouverture) : la case de la carte ne doit pas être la seule barrière",
      "la carte dit avant le clic que le numéro ne se reprend pas et qu'une erreur ne se corrige que par un avoir",
    ],
  },
});

/** Les exclusions qu'un écarté peut invoquer — liste FERMÉE, comme le reste. */
const EXCLUSIONS_ECART = Object.freeze([
  // ⚠️ AJOUTÉS LE 2026-09-14 avec le reclassement des trois ex-interdits : un
  // écarté « sous gardes » n'est pas écarté par prudence mais par ORDRE — les
  // conditions de son retour sont écrites dans son champ `gardes`, et elles
  // sont obligatoires. Un écarté de ces deux familles SANS gardes lève.
  "argent_sous_gardes",
  "tiers_sous_gardes",
  "E1",
  "E2",
  "E3",
  "E4",
  "E5",
  "hors_atteinte",
  "pii",
]);

/**
 * Le message de la levée. Il dit la date, la raison, ET la marche à suivre —
 * parce qu'un écarté, contrairement à un interdit absolu, PEUT revenir.
 */
function messageEcarte(nom, ecart) {
  return (
    `[leviers] « ${nom} » a été ÉCARTÉ le ${ecart.depuis} (${ecart.exclusion}) parce que : ${ecart.raison} ` +
    `— Si c'est voulu (décision Dimitri), retire-le d'ECARTES dans lib/operateur-leviers.js ET dis pourquoi, ` +
    `dans le commit et dans le commentaire du levier : un écarté qui revient sans son motif est un écarté qu'on ré-ouvrira. ` +
    `Un interdit absolu ne revient jamais ; un écarté peut revenir — à condition de le dire.`
  );
}

/**
 * La liste elle-même se vérifie au chargement : un écarté sans raison, sans
 * date ou sur une exclusion inconnue est exactement ce que la liste existe
 * pour empêcher. Et un nom qui serait à la fois interdit ET écarté ment sur
 * l'un des deux.
 */
function verifierEcartes(ecartes, interdits) {
  const noms = Object.keys(ecartes || {});
  if (!noms.length)
    throw new Error(
      "[leviers] ECARTES est VIDE — des dizaines de gestes ont été écartés avec leur raison (#1371, #1378, #1407), et trois interdits y ont été reclassés (2026-09-14) ; une liste vide ne protège rien. (Le compte exact n'est pas gravé ici : un nombre dans un message d'erreur vieillit et finit par mentir.)",
    );
  for (const nom of noms) {
    const e = ecartes[nom];
    if (!e || typeof e !== "object")
      throw new Error(
        `[leviers] écarté « ${nom} » : entrée absente ou mal formée`,
      );
    if (typeof e.raison !== "string" || e.raison.trim().length < 40)
      throw new Error(
        `[leviers] écarté « ${nom} » SANS RAISON écrite — un écarté sans raison est un écarté qu'on ré-ouvrira. Écris pourquoi, ou retire-le en le disant.`,
      );
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(e.depuis || "")))
      throw new Error(
        `[leviers] écarté « ${nom} » sans date (\`depuis\` AAAA-MM-JJ)`,
      );
    if (!EXCLUSIONS_ECART.includes(e.exclusion))
      throw new Error(
        `[leviers] écarté « ${nom} » : exclusion « ${e.exclusion} » inconnue (attendu : ${EXCLUSIONS_ECART.join(", ")})`,
      );
    // Les deux familles « sous gardes » portent les conditions de leur retour.
    // Sans elles, l'entrée dirait « on peut le rouvrir » sans dire à quel prix
    // — exactement l'oubli qui a fait descendre ces trois-là des interdits.
    if (/_sous_gardes$/.test(String(e.exclusion))) {
      if (!Array.isArray(e.gardes) || e.gardes.length === 0)
        throw new Error(
          `[leviers] écarté « ${nom} » est « ${e.exclusion} » mais ne dit PAS ses gardes. Un geste rouvert sans ses conditions est un geste ouvert : écris \`gardes\`, ou change d'exclusion.`,
        );
      const vide = e.gardes.findIndex(
        (g) => typeof g !== "string" || g.trim().length < 20,
      );
      if (vide >= 0)
        throw new Error(
          `[leviers] écarté « ${nom} » : la garde n° ${vide + 1} est vide ou trop courte pour dire quoi que ce soit.`,
        );
    }
    if (Object.prototype.hasOwnProperty.call(interdits || {}, nom))
      throw new Error(
        `[leviers] « ${nom} » est À LA FOIS interdit absolu et écarté — l'un des deux ment. Un interdit ne revient jamais, un écarté peut revenir : choisis.`,
      );
  }
}
verifierEcartes(ECARTES, INTERDITS_ABSOLUS);

// Les vagues vivent dans `leviers/_partage.js` (une seule source, lue aussi
// par le contrôle de forme) — déplacées telles quelles le 03/10 (F5).
const { VAGUES, DITS_AU_CATALOGUE } = require("./leviers/_partage.js");

/**
 * L'allow-list. FERMÉE : un levier absent d'ici n'est pas proposable, point.
 *
 * Chaque entrée décrit, elle n'exécute pas :
 *   endpoint        — l'URL que le NAVIGATEUR appellera (jamais le serveur)
 *   action          — le nom d'action attendu par cet endpoint
 *   args_autorises  — liste FERMÉE des clés que l'intention peut porter
 *   titre           — ce que la carte annonce
 *   constatable     — ce que Dimitri verra après le clic (garde E2)
 *   vague           — quand ce levier s'ouvre
 *   motif_requis    — le levier exige-t-il un motif écrit (cf. geste-motif.js)
 *   autonome        — peut-il s'enchaîner SANS clic ? (voir ci-dessous)
 */

// ══ L'AUTONOMIE — LES QUATRE QUESTIONS, ET ELLES SONT CUMULATIVES ═══════════
//
// Décision Dimitri, 2026-09-09 : « je maintiens ma règle "il propose, je
// clique" sur ce qui a des conséquences : l'argent, les messages à mes
// voyageurs, tout ce qui sort de chez moi. Mais je veux qu'il soit autonome sur
// ce qui n'a aucun risque. Le critère : rien à perdre si c'est fait de travers,
// et je vois le résultat juste après. »
//
// Un levier n'est `autonome: true` QUE si les quatre réponses sont oui :
//
//   1. RÉVERSIBLE EN UN GESTE — et le geste inverse existe déjà comme levier.
//      Pas « on pourrait le refaire à la main » : un chemin de retour réel.
//   2. NE SORT PAS DE CHEZ LUI — rien n'atteint un voyageur, un propriétaire,
//      Beds24, Stripe, une plateforme, une boîte mail. Un agent de ménage est
//      DEDANS (c'est l'entreprise) ; un voyageur est DEHORS.
//   3. CONSTATABLE TOUT DE SUITE — sur un écran qu'il ouvre le jour même. Un
//      effet différé n'est pas vérifiable, et ce qui n'est pas vérifiable ne
//      peut pas être autonome.
//   4. ZÉRO ARGENT — ni montant, ni prix, ni plancher, ni statut de paiement ou
//      de caution. Y compris l'argent ADJACENT : un plancher mal posé ne casse
//      rien, il fait juste perdre des nuits sans que personne ne le voie.
//
// ══ ET LA MÉCANIQUE AUTONOME A ÉTÉ ABANDONNÉE (2026-09-12) ═════════════════
//
// Dimitri avait donné son GO : « aucun ne sort de chez moi, aucun ne touche
// l'argent ». Puis, en me confiant l'arbitrage : « si un seul levier ne
// justifie pas la mécanique, abandonne-la et dis-le-moi. Je ne veux pas d'une
// pièce montée pour un cas unique. »
//
// ⚠️ MESURÉ AVANT DE TRANCHER, et c'est la mesure qui a décidé :
//
//   · L'autonomie exige DEUX choses : les quatre questions ci-dessus ET un
//     déclencheur honnête (un fait, en base, qui dise QUAND agir). Le tri des
//     233 actions a rendu quatre leviers d'un côté, quatre de l'autre — et
//     l'intersection est UN SEUL : `inbox_ia_escalation_resolve`.
//   · Ce levier clôt des escalades du copilote. Il y en a QUATRE ouvertes, et
//     aucune nouvelle depuis le 2026-08-22 — trois semaines.
//
// Construire la détection, l'exécution sans clic, la trace, le « défaire » et
// le plafond pour quatre lignes dormantes coûterait plus que tout ce qu'on en
// tirerait. Et ça ouvrirait un chemin par lequel quelque chose peut arriver
// SANS que Dimitri l'ait demandé — le seul chemin de ce genre dans tout le
// chantier, pour un gain nul.
//
// ⚠️ L'ALTERNATIVE EXISTE DÉJÀ, ET ELLE EST MEILLEURE : ce levier est ouvert en
// vague 2. Dimitri dit « clos les escalades qui n'ont plus d'objet »,
// l'opérateur prépare, il clique. Même résultat, zéro mécanique nouvelle, et
// il voit ce qui va se passer avant que ça se passe.
//
// Le champ `autonome` RESTE — il documente un tri qui a coûté du travail et
// qui redeviendra utile le jour où des faits se captent. Mais il ne sera pas
// câblé tant qu'un seul levier le justifie.
//
// ⚠️ CE QUE CE CHAMP NE FAIT PAS : il ne donne aucun pouvoir. Tant que
// `OPERATEUR_GESTES_AUTONOMES` n'existe pas, `autonome: true` n'est qu'une
// CLASSIFICATION — la machinerie reste « il propose, Dimitri clique ». On
// écrit le tri d'abord, on ouvre la vanne ensuite, et jamais l'inverse.
//
// ⚠️ ET LE DOUTE TRANCHE VERS `false`. Un levier dont on hésite sur l'une des
// quatre questions n'est pas autonome : le coût d'un « non » est un clic, le
// coût d'un « oui » de trop est une chose faite dans le dos de Dimitri.
// ══ L'AGRÉGATION PAR FAMILLE ═══════════════════════════════════════════════
//
// ⚠️ UN FICHIER PAR FAMILLE (`lib/leviers/*.js`), et non un registre géant :
// les 112 leviers restants (inventaire du 2026-09-09) se déclarent EN
// PARALLÈLE, et six personnes qui écrivent dans le même objet, ce sont six
// conflits — dont un résolu de travers sur une allow-list donnerait un argument
// autorisé par accident.
//
// ⚠️ ET UN DOUBLON LÈVE AU CHARGEMENT. Deux familles qui déclarent le même nom,
// c'est l'une qui écrase l'autre en silence : la carte annoncerait un titre et
// exécuterait l'autre action. On préfère un serveur qui refuse de démarrer.
// ⚠️ `DITS_AU_CATALOGUE` N'EST PLUS RECOPIÉ ICI (F5, 03/10) : il vient de
// `leviers/_partage.js` (require plus haut), que `_forme.js` lit aussi. Le
// piège d'origine tient toujours — `_forme.js` tire `../app-carte.js`, sans
// miroir dans `src/` : ce registre ne doit JAMAIS l'importer, sous peine de
// casser le registre CÔTÉ NAVIGATEUR pendant que les tests serveur restent
// verts. `_partage.js`, lui, ne requiert rien.

// F5 (03/10/2026) — UNE LIGNE PAR FAMILLE, ET RIEN D'AUTRE. Le nom de la
// famille n'est plus écrit ici : il se LIT dans sa déclaration, l'export
// `LEVIERS_<FAMILLE>` de son fichier (« LEVIERS_STOCK » → « stock »). Ajouter
// une famille = un fichier `lib/leviers/<famille>.js` et une ligne ici ; tout
// ce que la famille déclare (ses leviers, les phrases de ses booléens) est
// rassemblé à partir de là, jamais recopié.
const MODULES_FAMILLES = [
  require("./leviers/operateur.js"),
  require("./leviers/pricing.js"),
  require("./leviers/messages.js"),
  require("./leviers/voyageurs.js"),
  require("./leviers/menage.js"),
  require("./leviers/logements.js"),
  require("./leviers/livret.js"),
  require("./leviers/params.js"),
  require("./leviers/taches.js"),
  // Ouverte le 2026-09-15 sur décision Dimitri (« renvoyer une facture déjà
  // émise : oui, vague 9 ») — le sujet `argent` cesse d'être vide.
  require("./leviers/argent.js"),
  // ⚠️ PREMIÈRE FAMILLE HORS `/api/beds24-mapping` (2026-09-16, lot 5) : ses
  // leviers visent `/api/stock`. Rien ne change ici — l'endpoint est un champ
  // du levier, et la liste blanche vit dans `leviers/_forme.js`.
  require("./leviers/stock.js"),
];

/**
 * `[famille, table des leviers]`, lus dans la déclaration d'un module.
 * Exactement UN export `LEVIERS_<FAMILLE>` : zéro, c'est une famille vide qui
 * se croirait déclarée ; deux, c'est un nom de famille à choisir au hasard.
 */
function _familleDe(mod) {
  const cles = Object.keys(mod || {}).filter((k) => /^LEVIERS_[A-Z]+$/.test(k));
  if (cles.length !== 1)
    throw new Error(
      `[leviers] une famille déclare UN export LEVIERS_<FAMILLE> — trouvé : ${cles.join(", ") || "aucun"}`,
    );
  return [cles[0].slice("LEVIERS_".length).toLowerCase(), mod[cles[0]]];
}

const FAMILLES = MODULES_FAMILLES.map(_familleDe);

/**
 * Une table que chaque famille déclare pour elle-même (`nomExport`), réunie
 * en une seule. ⚠️ UNE CLÉ DÉCLARÉE DEUX FOIS LÈVE AU CHARGEMENT, comme un
 * levier en double : deux familles qui écrivent la même clé, c'est l'une qui
 * écrase l'autre en silence. Une famille qui n'a rien à déclarer n'exporte
 * rien.
 */
function _rassemblerTable(nomExport) {
  const out = {};
  const origine = {};
  MODULES_FAMILLES.forEach((mod, i) => {
    const fam = FAMILLES[i][0];
    const table = (mod && mod[nomExport]) || {};
    for (const cle of Object.keys(table)) {
      if (Object.prototype.hasOwnProperty.call(out, cle))
        throw new Error(
          `[leviers] ${nomExport} « ${cle} » déclaré deux fois : ${origine[cle]} et ${fam}`,
        );
      out[cle] = table[cle];
      origine[cle] = fam;
    }
  });
  return Object.freeze(out);
}

function _agreger(familles) {
  const out = {};
  const origine = {};
  for (const [fam, table] of familles) {
    for (const nom of Object.keys(table || {})) {
      if (Object.prototype.hasOwnProperty.call(out, nom))
        throw new Error(
          `[leviers] « ${nom} » déclaré deux fois : ${origine[nom]} et ${fam}`,
        );
      // ⚠️ Un levier interdit ne s'ajoute pas « au cas où ». Il lève, pour que
      // la faute se voie au démarrage et non le jour où l'écran le propose.
      if (Object.prototype.hasOwnProperty.call(INTERDITS_ABSOLUS, nom))
        throw new Error(
          `[leviers] « ${nom} » est un INTERDIT ABSOLU : ${INTERDITS_ABSOLUS[nom]}`,
        );
      // ⚠️ Un ÉCARTÉ qui revient lève AUSSI — mais son message dit comment
      // le faire entrer proprement, parce que lui, il PEUT revenir.
      if (Object.prototype.hasOwnProperty.call(ECARTES, nom))
        throw new Error(messageEcarte(nom, ECARTES[nom]));
      out[nom] = Object.freeze({ ...table[nom], famille: fam });
      origine[nom] = fam;
    }
  }
  return Object.freeze(out);
}

const LEVIERS = _agreger(FAMILLES);

/** @returns {object|null} la description d'un levier, ou null s'il n'existe pas. */
// ══ LES CHEMINS POINTÉS — `achat.csId`, `achat.prixTotal` (lot 4) ═══════════
//
// Un identifiant résolu ou une valeur TAPÉE sur la carte peut vivre DANS un
// argument composé. Ces trois helpers vivent ici, et pas dans
// `reperes-geste.js`, parce que le NAVIGATEUR en a besoin aussi
// (`src/lib/geste-executer.js` pose le prix tapé) : ce fichier est miroité
// dans `src/lib/`, `reperes-geste.js` ne l'est pas. Maillons clonés, jamais
// mutés ; un tableau n'est jamais un maillon.

/** La valeur sous un chemin pointé, ou `undefined` si un maillon manque. */
function lireChemin(obj, chemin) {
  const parts = String(chemin || "").split(".");
  let cur = obj;
  for (const p of parts) {
    if (!cur || typeof cur !== "object" || Array.isArray(cur)) return undefined;
    if (!Object.prototype.hasOwnProperty.call(cur, p)) return undefined;
    cur = cur[p];
  }
  return cur;
}

/** Le même objet, la valeur posée sous le chemin — maillons clonés. */
function poserChemin(obj, chemin, valeur) {
  const parts = String(chemin || "").split(".");
  const racine = obj && typeof obj === "object" ? { ...obj } : {};
  let cur = racine;
  for (let i = 0; i < parts.length - 1; i += 1) {
    const p = parts[i];
    const suivant =
      cur[p] && typeof cur[p] === "object" && !Array.isArray(cur[p])
        ? { ...cur[p] }
        : {};
    cur[p] = suivant;
    cur = suivant;
  }
  cur[parts[parts.length - 1]] = valeur;
  return racine;
}

/**
 * UNE SAISIE DE LA CARTE S'APPLIQUE-T-ELLE À CES ARGUMENTS ? (lot 6)
 *
 * `args_saisis[chemin].si = { champ: ["montant"] }` : la valeur se tape sur la
 * carte SEULEMENT quand `args.champ` vaut l'une des valeurs nommées. Sans `si`,
 * elle s'applique toujours. Partagé avec le navigateur (miroir `src/lib/`) :
 * le serveur s'en sert pour retirer ce que le modèle a composé, la carte pour
 * afficher ou non le champ, `geste-executer` pour poser ce qui a été tapé —
 * trois lecteurs, UNE règle. Une condition qui ne peut pas se lire (argument
 * absent) ne s'applique PAS : on ne demande pas de taper un montant pour un
 * champ qu'on ne connaît pas.
 */
function saisieSApplique(decl, args) {
  if (!decl || typeof decl !== "object") return false;
  if (!decl.si || typeof decl.si !== "object") return true;
  for (const [arg, valeurs] of Object.entries(decl.si)) {
    const v = lireChemin(args && typeof args === "object" ? args : {}, arg);
    if (!Array.isArray(valeurs) || !valeurs.includes(v)) return false;
  }
  return true;
}

/** Le même objet, la clé retirée sous le chemin — maillons clonés. */
function retirerChemin(obj, chemin) {
  if (lireChemin(obj, chemin) === undefined) return obj;
  const parts = String(chemin || "").split(".");
  const racine = { ...obj };
  let cur = racine;
  for (let i = 0; i < parts.length - 1; i += 1) {
    cur[parts[i]] = { ...cur[parts[i]] };
    cur = cur[parts[i]];
  }
  delete cur[parts[parts.length - 1]];
  return racine;
}

function lireLevier(nom) {
  if (
    typeof nom !== "string" ||
    !Object.prototype.hasOwnProperty.call(LEVIERS, nom)
  ) {
    return null;
  }
  return LEVIERS[nom];
}

/**
 * Les leviers ouverts pour une vague donnée (et les précédentes).
 * `vagueMax` vient d'une variable d'environnement : ouvrir une vague est une
 * décision de Dimitri, pas un commit.
 */
function leviersOuverts(vagues) {
  // ⚠️ UN ENSEMBLE, OU UN SEUIL — et il fallait les deux. Un seuil ne sait pas
  // sauter une marche : `leviersOuverts(9)` ouvrait la vague 4 (l'argent) avec
  // la 9, alors que Dimitri se l'est expressément réservée. Et rien à l'écran
  // ne l'aurait dit : il aurait fallu que quelqu'un demande un prix pour s'en
  // apercevoir.
  if (vagues instanceof Set) {
    // Levier par levier (2026-09-15) : `.choisis` (Map<vague, Set<nom>>) porte
    // les leviers ouverts UN PAR UN dans une vague candidate — cf.
    // vaguesOuvertes() dans operateur-intention.js.
    const choisis = vagues.choisis instanceof Map ? vagues.choisis : null;
    const unParUn = (k) => choisis?.get(LEVIERS[k].vague)?.has(k) === true;
    if (!vagues.size && !(choisis && choisis.size)) return [];
    return Object.keys(LEVIERS).filter(
      (k) => vagues.has(LEVIERS[k].vague) || unParUn(k),
    );
  }
  const max = Number(vagues);
  if (!Number.isFinite(max) || max < 1) return [];
  return Object.keys(LEVIERS).filter((k) => LEVIERS[k].vague <= max);
}

/**
 * Filtre les arguments d'une intention selon la liste FERMÉE du levier.
 *
 * ⚠️ C'est la garde qui empêche l'opérateur de CHOISIR. Un argument hors liste
 * n'est pas ignoré silencieusement : la fonction rend `null`, et l'intention
 * entière est refusée. Ignorer en silence laisserait passer une intention à
 * moitié comprise — pire qu'un refus franc.
 *
 * @returns {object|null} les args retenus, ou null si l'un d'eux est interdit
 */
function filtrerArgs(nomLevier, args) {
  const levier = lireLevier(nomLevier);
  if (!levier) return null;
  if (!args || typeof args !== "object" || Array.isArray(args)) return null;
  const autorises = new Set(levier.args_autorises);
  const out = {};
  for (const cle of Object.keys(args)) {
    if (!autorises.has(cle)) return null;
    out[cle] = args[cle];
  }
  return out;
}

// ══ CE QU'UN GESTE NE PEUT PAS OMETTRE — `args_indispensables` (revue #1440) ══
//
// Un argument AUTORISÉ n'est pas un argument SUFFISANT. `pricing_set_manual`
// accepte `min_stay` seul dans sa liste fermée — et le serveur exige
// `price_eur` (400 `prix_invalide`). Le levier déclare donc ce qu'il ne peut
// pas omettre, et cette fonction est le SEUL juge, lue par trois côtés :
// `lireGeste` (serveur, la carte reçoit `manque_indispensable`), la carte
// (miroir `src/lib/`, bouton gris + phrase) et `executerGeste` (refus au
// clic, fail-closed). Trois lecteurs, une ligne — aucune règle recopiée.
//
// ⚠️ LA PHRASE EST GLOSÉE, JAMAIS UN NOM D'ARGUMENT. « il lui faut
// price_eur » est du jargon sur une carte ; « il lui faut un prix » se lit.
// Un test refuse tout argument indispensable sans libellé ici.
//
// ⚠️ ET CETTE TABLE EST GLOBALE, PAS RANGÉE PAR LEVIER (revue #1478). La clé
// est le NOM DE L'ARGUMENT, pas le couple levier+argument : `nom` vaut « un
// nom » pour TOUT levier qui déclare `nom` indispensable — `logement_creer`
// aujourd'hui, un autre demain. C'est voulu (un argument qui porte le même
// nom veut dire la même chose, sinon c'est le nom qui est mauvais), mais ça
// veut dire qu'ajouter une glose ici touche tout le monde : la relire depuis
// CHAQUE levier concerné, jamais depuis celui qu'on est en train d'écrire.
// Un test parcourt les leviers et vérifie que chaque glose se lit derrière
// « il lui faut … ».
const ARGS_LIBELLES = Object.freeze({
  price_eur: "un prix",
  min_stay: "un séjour minimum",
  base_price: "un prix de base",
  min_price: "un plancher",
  // ⚠️ LES HUIT DU 2026-09-17 — corrections d'un mois de facturation ET
  // lignes de compta. Une glose reste une glose : « il lui faut un montant »
  // se lit, « il lui faut montant » est un nom d'argument déguisé en phrase.
  // `nouvelle` sert aux deux familles (montant côté facturation, valeur d'un
  // champ côté compta) : la glose générique est la seule vraie pour les deux.
  nouvelle: "une nouvelle valeur",
  libelle: "un libellé",
  montant: "un montant",
  // Une réouverture sans dates ne vise rien : le serveur ne saurait pas quel
  // blocage on lui demande de rouvrir (2026-09-17).
  start_date: "une date de début",
  end_date: "une date de fin",
  sorte: "une charge fixe ou une dépense",
  ligne_id: "une ligne à corriger",
  champ: "un champ à corriger",
  // ⚠️ LES SIX DE L'INSCRIPTION D'UN LOGEMENT (revue #1478). Le serveur les
  // exige tous les six ; sans glose, la carte aurait recopié ses noms de
  // champs — « il lui faut commission_taux » — au lieu de les dire.
  // ⚠️ LES SIX DE L'INSCRIPTION D'UN LOGEMENT (revue #1478) — ajoutés POUR
  // `logement_creer`, mais valables PARTOUT (§ la table est globale). `nom` et
  // `statut` sont les deux plus exposés : `proprietaire_creer` et
  // `site_page_publication_set` les acceptent déjà, et le jour où l'un des
  // deux les déclarera indispensables, il héritera de ces gloses sans qu'on
  // le décide. « il lui faut un nom » / « il lui faut un statut » s'y lisent —
  // c'est ce qui rend l'héritage acceptable, et c'est ce que le test relit.
  proprietaire_id: "un propriétaire",
  nom: "un nom",
  statut: "un statut",
  commission_taux: "un taux de commission",
  adresse_complete: "une adresse",
  capacite_personnes: "un nombre de personnes",
  // ══ LE RESTE DU GLOSSAIRE — POUR L'ÉCRAN, PAS SEULEMENT POUR LE MANQUE ══
  //
  // ⚠️ AJOUTÉS LE 2026-09-22 parce que la CARTE lit désormais cette table.
  // Elle affichait ses arguments sous leur nom de code — « champ
  // commission_taux · nouvelle 20 » —, c'est-à-dire des noms de colonnes
  // dans un écran qui n'en dit jamais. Un test balaie tous les
  // `args_autorises` de tous les leviers et refuse celui qui n'a pas sa
  // glose : la table est une LISTE FERMÉE, un argument neuf ne peut plus
  // arriver à l'écran sous son nom technique.
  //
  // ⚠️ ET LA RÈGLE DE RÉDACTION NE CHANGE PAS : article indéfini, un mot que
  // Dimitri dirait. « un taux de commission », jamais « commission_taux ».
  // ⚠️ SONT ABSENTS, VOLONTAIREMENT, LES IDENTIFIANTS (`*_id`, `*_ids`,
  // `threadId`…) : ils ne s'affichent PAS du tout (la carte masque la ligne),
  // parce qu'un identifiant ne se lit pas — ce qu'il désigne se dit par son
  // nom. Le test les exclut pour la même raison.
  achat: "un achat",
  // ⚠️ LA CARTE NOMME L'ARTICLE D'UN GESTE DE STOCK (revue #1501, M1) — le
  // nom lu par l'aperçu, jamais `cs9`. « article » n'est pas un argument :
  // c'est la ligne que la carte AJOUTE pour dire ce que `csId` désigne.
  article: "un article",
  actif: "une activation ou une désactivation",
  active: "une activation ou une désactivation",
  is_active: "une activation ou une désactivation",
  enabled: "une activation ou une désactivation",
  archived: "un archivage",
  published: "une publication ou un retrait",
  retirer: "un retrait",
  confirmer: "une confirmation",
  regenerate: "une régénération",
  overwrite_existing: "un écrasement de ce qui existe",
  catchup_mode: "un rattrapage des envois passés",
  getyourguide_enabled: "une activation des activités GetYourGuide",
  address_full: "une adresse",
  adresse: "une adresse",
  agent_email: "un agent",
  alt_text: "une description de la photo",
  answer: "une réponse",
  available_languages: "des langues proposées",
  blocs: "des blocs de message",
  body: "un message",
  body_en: "un message en anglais",
  body_es: "un message en espagnol",
  body_fr: "un message en français",
  canal: "un canal",
  channel: "un canal",
  capacite_chambres: "un nombre de chambres",
  capacite_salles_de_bain: "un nombre de salles de bain",
  surface_m2: "une surface",
  nom_complet: "un nom complet",
  category: "une catégorie",
  subcategory: "une sous-catégorie",
  category_order: "un ordre des catégories",
  checkin: "une heure d'arrivée",
  checkout: "une heure de départ",
  choix: "un choix",
  city: "une ville",
  ville: "une ville",
  cle: "une alerte précise",
  code: "une ligne du mois",
  code_postal: "un code postal",
  postal_code: "un code postal",
  country: "un pays",
  conditions: "des conditions d'envoi",
  confirmStage: "une étape de confirmation",
  contact: "un contact",
  contact_email: "un e-mail de contact",
  contact_phone: "un téléphone de contact",
  emergency_contact: "un contact d'urgence",
  content: "un contenu",
  content_type: "un type de contenu",
  controleur_email: "un contrôleur",
  date: "une date",
  // Lot 3 des repères : le verdict d'une dégradation, dit par Dimitri.
  verdict: "un verdict",
  date_from: "une date de début",
  date_to: "une date de fin",
  decision: "une décision",
  default_language: "une langue par défaut",
  depot: "un dépôt",
  description: "une description",
  echeance: "une échéance",
  email: "un e-mail",
  famille: "une famille",
  feedback_threshold: "un seuil d'avis",
  reviews_threshold: "un seuil d'avis",
  field: "un champ à corriger",
  fields: "des champs à corriger",
  flag: "un marquage",
  geste: "un geste",
  google_maps_url: "un lien Google Maps",
  tisseo_url: "un lien Tisséo",
  website_url: "un site web",
  logo_url: "un logo",
  public_url: "un lien public",
  video_url: "une vidéo",
  photo_urls: "des photos",
  gyg_number_of_items: "un nombre d'activités",
  hour: "une heure",
  hours: "des horaires",
  icon: "une icône",
  issue: "un problème signalé",
  key: "une clé",
  label: "un libellé",
  label_en: "un libellé en anglais",
  label_es: "un libellé en espagnol",
  label_fr: "un libellé en français",
  logement: "un logement",
  mois: "un mois",
  motif: "un motif",
  raison: "une raison",
  reason: "une raison",
  multiplier: "un multiplicateur",
  name: "un nom",
  nom_famille: "un nom de famille",
  prenom: "un prénom",
  telephone: "un téléphone",
  offset_days: "un décalage en jours",
  op: "une opération",
  order_index: "une position dans la liste",
  ordered_slugs: "un ordre",
  paye_par: "un payeur",
  periode: "une période",
  plateforme: "une plateforme",
  poi: "un lieu",
  portee_type: "une portée",
  scope: "une portée",
  priority: "une priorité",
  prises: "des prises de stock",
  qty: "une quantité",
  qtyDispo: "une quantité disponible",
  seuilMin: "un seuil minimum",
  question: "une question",
  reglage: "un réglage",
  section_type: "une section",
  slug: "un gabarit",
  social_networks: "des réseaux sociaux",
  status: "un statut",
  statuts: "des statuts",
  storage_path: "un fichier déposé",
  subject: "un objet de message",
  subject_en: "un objet en anglais",
  subject_es: "un objet en espagnol",
  subject_fr: "un objet en français",
  target_agent_email: "un agent destinataire",
  text_fr: "un texte en français",
  texte: "un texte",
  theme_color_primary: "une couleur principale",
  timezone: "un fuseau horaire",
  title: "un titre",
  titre: "un titre",
  token: "un jeton",
  trigger_fixed_date: "une date d'envoi fixe",
  trigger_offset_unit: "une unité de décalage",
  trigger_offset_value: "un décalage",
  trigger_time: "une heure d'envoi",
  trigger_type: "un type de déclenchement",
  type: "un type",
  value: "une valeur",
  visibility_conditions: "des conditions d'affichage",
  weather_city: "une ville pour la météo",
  weather_lat: "une latitude pour la météo",
  weather_lng: "une longitude pour la météo",
  welcome_subtitle: "un sous-titre d'accueil",
  wifi_networks: "des réseaux wifi",
});

// ══ LES BOOLÉENS, EN MOTS — JAMAIS `true` / `false` À L'ÉCRAN ══════════════
//
// Mesuré en prod le 2026-09-22 (« Lève le verrou du livret pour la résa de
// Sur un Nuage du 26 septembre ») : la carte disait
//
//     une activation ou une désactivation   true
//
// Le libellé venait du glossaire, la valeur du `String(true)` de la carte. Un
// booléen ne se lit pas : c'est ce qu'il FAIT qui se lit — « Lever le
// verrou » ou « Remettre le verrou ». Et ça dépend du LEVIER, pas du nom de
// l'argument : `active` lève un verrou sur `livret_override_set` et allume un
// multiplicateur sur `multiplier_set`. La table est donc indexée par
// `levier.argument`, avec la phrase de chaque valeur.
//
// ⚠️ LISTE FERMÉE, GARDÉE PAR UN TEST : `NOMS_BOOLEENS` dit quels arguments
// du registre portent un booléen (le registre ne déclare pas de types — c'est
// la lecture des handlers qui le dit), et le test refuse tout couple
// `levier.argument` de cette liste sans phrase ici. Un booléen qui n'aurait
// pas la sienne se lit « oui »/« non » derrière son libellé (`motsDuBooleen`),
// jamais `true`/`false` — le repli est lisible, mais le test l'interdit.
//
// ⚠️ LA PHRASE DIT LE GESTE À L'INFINITIF, comme le titre d'un levier : c'est
// une ligne de carte (« Lever le verrou »), pas une glose de manque. Et elle
// se suffit : un booléen est une PHRASE, pas un couple libellé/valeur — un
// « : oui » derrière « Remettre le verrou » comme derrière « Lever le
// verrou » ne distinguait rien (revue, mineur d).
const NOMS_BOOLEENS = Object.freeze([
  "retirer",
  "overwrite_existing",
  "active",
  "is_active",
  "regenerate",
  "catchup_mode",
  "confirmer",
  "archived",
  "actif",
  "published",
  "getyourguide_enabled",
  "enabled",
  "value",
]);
// F5 (03/10/2026) — CHAQUE PHRASE VIT DANS LA FAMILLE DE SON LEVIER
// (`lib/leviers/<famille>.js`, export `BOOLEENS_LIBELLES`), déplacée telle
// quelle d'ici : la déclarer, c'est écrire à côté du levier qu'elle dit. Le
// registre ne fait que RASSEMBLER — une clé déclarée deux fois lève au
// chargement, comme un levier en double.
const BOOLEENS_LIBELLES = _rassemblerTable("BOOLEENS_LIBELLES");

/**
 * Un argument booléen, dit : `{libelle, valeur}` pour une ligne de carte.
 *
 * Avec sa phrase : « Lever le verrou », seule (`valeur: ""`). Sans (repli,
 * interdit par le test) : le libellé du glossaire et « oui »/« non » — jamais
 * `true`/`false`.
 *
 * @param {string} nomLevier
 * @param {string} arg
 * @param {boolean} valeur
 * @returns {{libelle:string, valeur:string}}
 */
function motsDuBooleen(nomLevier, arg, valeur) {
  const v = valeur === true;
  const phrase = BOOLEENS_LIBELLES[`${nomLevier}.${arg}`];
  if (phrase) return { libelle: v ? phrase.oui : phrase.non, valeur: "" };
  return { libelle: ARGS_LIBELLES[arg] || arg, valeur: v ? "oui" : "non" };
}

// ══ CE QUE LA PHRASE DU MANQUE ÉNUMÈRE — ET CE QU'ELLE N'ÉNUMÈRE PAS ══════
//
// ⚠️ SÉPARÉ DU GLOSSAIRE LE 2026-09-22, ET C'EST LE POINT DÉLICAT. La phrase
// « ce réglage ne sait pas poser X seul : il lui faut Y » listait TOUT
// argument composé qui avait une glose. Tant que la table ne glosait que des
// réglages, ça marchait. Depuis qu'elle glose aussi ce que la CARTE affiche
// (dates, mois, ville, motif…), la même phrase serait devenue « ne sait pas
// poser une date de début et une date de fin et une raison seul » : une
// énumération de la cible, là où Dimitri attend le réglage qui manque.
//
// Cette liste est donc GELÉE sur ce qu'elle énumérait la veille — élargir le
// glossaire ne change plus une seule phrase. Y ajouter une clé est une
// décision de rédaction, jamais un effet de bord.
const ARGS_ENUMERABLES = Object.freeze(
  new Set([
    "price_eur",
    "min_stay",
    "base_price",
    "min_price",
    "nouvelle",
    "libelle",
    "montant",
    "start_date",
    "end_date",
    "sorte",
    "ligne_id",
    "champ",
    "proprietaire_id",
    "nom",
    "statut",
    "commission_taux",
    "adresse_complete",
    "capacite_personnes",
  ]),
);

/**
 * Le premier argument indispensable ABSENT du geste composé, glosé — ou null.
 *
 * @param {string} nomLevier
 * @param {object} args — les arguments composés (déjà filtrés ou non)
 * @returns {{arg:string, phrase:string}|null}
 */
function manqueIndispensable(nomLevier, args) {
  const levier = lireLevier(nomLevier);
  if (!levier) return null;
  const a = args && typeof args === "object" ? args : {};
  const present = (k) =>
    Object.prototype.hasOwnProperty.call(a, k) &&
    a[k] !== null &&
    a[k] !== undefined &&
    a[k] !== "";
  // ⚠️ UNE SAISIE CONDITIONNELLE HORS DE SA CONDITION EST INDISPENSABLE
  // (lot 6). `compta_ligne_champ_set.nouvelle` se tape sur la carte quand le
  // champ est « montant » ; pour un libellé, c'est le modèle qui la compose,
  // et sans elle le serveur répondrait 400 sous le doigt. Elle ne peut pas
  // être dans `args_indispensables` (le registre l'interdit : la carte ne
  // viendrait jamais quand elle se tape) — c'est donc ICI, par la même
  // déclaration, qu'elle redevient exigée. Un chemin pointé n'y entre pas :
  // `args_indispensables` est premier-niveau par construction.
  const saisiesHorsCondition = Object.entries(levier.args_saisis || {})
    .filter(
      ([chemin, decl]) =>
        !chemin.includes(".") && decl && decl.si && !saisieSApplique(decl, a),
    )
    .map(([chemin]) => chemin);
  const indispensables = [
    ...(Array.isArray(levier.args_indispensables)
      ? levier.args_indispensables
      : []),
    ...saisiesHorsCondition,
  ];
  if (!indispensables.length) return null;
  const horsCondition = new Set(saisiesHorsCondition);
  for (const arg of indispensables) {
    if (present(arg)) continue;
    const libelle = ARGS_LIBELLES[arg] || arg;
    // ══ UNE SAISIE HORS CONDITION A SA PROPRE PHRASE (revue adverse #1508,
    //    M7) ═══════════════════════════════════════════════════════════════
    //
    // ⚠️ MESURÉE, ET ILLISIBLE : « ce réglage ne sait pas poser un champ à
    // corriger et une ligne à corriger et une charge fixe ou une dépense
    // seul : il lui faut une nouvelle valeur ». La formule générique énumère
    // ce que le geste POSE — elle vaut pour un réglage qui en porte un autre
    // (« poser un séjour minimum seul : il lui faut un prix »), pas pour une
    // valeur que le modèle doit ÉCRIRE sur une cible déjà désignée. Règle
    // Dimitri du 15/09 : si ma mère ne comprend pas, réécris.
    //
    // ⚠️ SANS PRONOM QUI S'ACCORDE : « dites-la-moi » suppose le féminin. La
    // glose vient du registre et peut changer de genre ; la phrase, elle, ne
    // doit pas être à réécrire le jour où ça arrive.
    if (horsCondition.has(arg))
      return {
        arg,
        phrase: `il me faut ${libelle} : dites-moi quoi écrire, et je le prépare`,
      };
    // Ce que le geste POSE sans lui — les autres réglages composés qui ont
    // un libellé (jamais les cibles : logement, dates). « ce réglage ne sait
    // pas poser un séjour minimum seul : il lui faut un prix ».
    const autres = Object.keys(a)
      .filter((k) => k !== arg && present(k) && ARGS_ENUMERABLES.has(k))
      .map((k) => ARGS_LIBELLES[k]);
    // ⚠️ ET L'ÉNUMÉRATION SE LIT (même revue). « X et Y et Z seul » n'est pas
    // du français : au-delà d'un élément, virgules puis « et », et « seul »
    // disparaît — il ne s'accorde plus.
    const enumere =
      autres.length > 1
        ? `${autres.slice(0, -1).join(", ")} et ${autres[autres.length - 1]}`
        : autres[0];
    const phrase = autres.length
      ? autres.length > 1
        ? `ce réglage ne sait pas poser ${enumere} sans ${libelle}`
        : `ce réglage ne sait pas poser ${enumere} seul : il lui faut ${libelle}`
      : `ce réglage ne part pas sans ${libelle}`;
    return { arg, phrase };
  }
  return null;
}

// ══ L'INDEX COMPACT — CE QUE LE MODÈLE VOIT ════════════════════════════════
//
// ⚠️ LE POINT D'ARCHITECTURE DU CHANTIER. 116 leviers décrits en entier
// (arguments, constatable, motif) feraient exploser le prompt — et un catalogue
// tronqué au milieu ferait promettre à l'opérateur des gestes qu'il ne sait pas
// composer. Le défaut serait plausible : il répondrait « je m'en occupe », et
// rien ne partirait.
//
// Donc le modèle reçoit UNE LIGNE par levier — son nom, ses arguments, son
// titre. Pas le `constatable` (c'est pour la carte de Dimitri, pas pour lui),
// pas le raisonnement d'autonomie, pas la vague.
//
// ⚠️ ET LA VALIDATION NE BOUGE PAS D'UN POUCE. `filtrerArgs` reste la garde :
// un argument hors liste refuse l'intention ENTIÈRE, côté serveur. L'index est
// une aide à la composition, jamais une autorisation — c'est pour ça qu'on peut
// se permettre de le raccourcir.
// ⚠️ RELEVÉ DE 9 000 À 15 000 LE 2026-09-09, et le calcul est explicite :
// depuis que chaque argument à liste fermée montre SES VALEURS (le défaut
// « adresse » vs « adresse_complete »), l'index a grossi d'un tiers — 8 922
// caractères pour 74 leviers, et il en reste une quarantaine à déclarer. À
// 9 000, la coupe serait tombée dès la vague suivante, et un geste coupé du
// catalogue est un geste que l'opérateur croit inexistant.
//
// Le coût est réel : ~3 500 jetons par question, à chaque question. On l'assume
// parce que l'inverse — un catalogue qui rétrécit en silence — coûte des
// refus injustifiés que personne ne saurait expliquer.
// ⚠️ RELEVÉ DE 15 000 À 18 000 LE 2026-09-16, et le chiffre sort d'une mesure,
// pas d'un arrondi. Dimitri : « relève le plafond. 250 jetons par question,
// c'est rien comparé au risque : un catalogue qui coupe en silence, c'est le
// même motif que tout ce qu'on corrige depuis deux jours. Et mets une alerte :
// si on repasse sous 10 % de marge, je veux le savoir avant que ça coupe. »
//
// ⚠️ LE PREMIER RELEVÉ (15 000 → 17 000) A EMPIRÉ CE QU'IL DEVAIT CORRIGER, et
// c'est la leçon à ne pas perdre. Le budget en plus n'allait PAS aux gestes
// qui manquaient : `catalogueAvecDits` sert les phrases de Dimitri jusqu'à
// épuiser le budget AVANT d'écrire le bloc « pas encore ouverts », donc les
// deux mille caractères ont financé une DEUXIÈME phrase par geste ouvert et
// mangé les fermés. Mesuré à vagues égales ({1,2,3,9}, écran calendrier) :
// 20 fermés sur 21 nommés à 15 000 · 2 sur 21 à 16 000 · 9 sur 21 à 17 000 —
// et « Modifier un prix » (le geste du défaut de prod du 2026-09-15) SORTAIT
// du catalogue dès 16 000. Un plafond ne se relève pas sans regarder qui
// dépense : c'est la réserve (`RESERVE_FERMES`, plus bas) qui répare, pas le
// chiffre.
//
// ⚠️ LE CHIFFRE, UNE FOIS LA RÉSERVE POSÉE. On mesure le PLANCHER — tout ce
// qui est DÛ au modèle : l'index complet + une phrase par geste ouvert + tous
// les gestes fermés nommés. Balayage des 1 905 combinaisons (toutes les parties
// non vides des vagues déclarées × tous les écrans) : le pire pèse
// **15 806 caractères**. D'où les paliers honnêtes :
//
//   16 000 → la garantie tient, marge 1,2 % : le prochain levier déclaré la
//            reprend. C'est le palier qui « marche aujourd'hui » et qui ment
//            demain ;
//   17 000 → marge 7,0 %, c'est-à-dire SOUS le seuil que Dimitri a fixé le
//            jour même — on livrerait une alerte rouge à la naissance ;
//   18 000 → marge 12,2 %. Le premier palier qui respecte sa propre règle.
//
// Le coût est de ~780 jetons par question (et non 250 : l'estimation portait
// sur +2 000 caractères, la réalité en demande 3 000). On l'assume parce que
// l'inverse se paie en refus injustifiés que personne ne sait expliquer. Si
// Dimitri préfère payer moins, le levier n'est PAS de relâcher l'alerte : c'est
// d'alléger le catalogue (titres plus courts, moins de `dit_comme` sur les
// gestes les moins demandés) — le plancher baisse, le plafond suit.
//
// ⚠️ ET LA MARGE EST DÉSORMAIS SURVEILLÉE : `src/lib/operateur-catalogue-marge.test.js`
// échoue sous 10 % de marge sur ce plancher — pour que la décision de relever
// se prenne AVANT que la coupe ne tombe, jamais après.
// ⚠️ RELEVÉ À 20 700 LE 2026-09-17 (décision Dimitri). Motif, mot pour mot :
// « 2 à 5 jetons par question, c'est rien comparé à un geste qui disparaît
// sans que je le sache. »
//
// ⚠️ LE CHIFFRE DE 2 À 5 JETONS ÉTAIT FAUX, ET IL FAUT LE DIRE : il valait
// pour un relèvement de 100 caractères. Le vrai coût de ce palier est de
// ~1 300 jetons par question (18 000 → 20 700, à 2,03 caractères par jeton —
// le français accentué coûte le double de l'anglais). La décision tient quand
// même, et elle tient MIEUX depuis la mise en cache du prompt : le catalogue
// vit dans la région stable-par-écran, donc ces jetons sont relus à 0,1× du
// tarif d'entrée dès le second appel. Soit ≈ 0,07 centime par question, pas
// 0,7. Sans le cache, c'était dix fois plus.
//
// ⚠️ ET LA MARGE SE MESURE SUR LE PLANCHER RECONSTRUIT, JAMAIS SUR LA
// LONGUEUR RENDUE. Le rendu est DÉJÀ tronqué à ce plafond : mesurer sa taille
// pour en déduire une marge est circulaire, et rend toujours « il reste un
// cheveu ». `src/lib/operateur-catalogue-marge.test.js` reconstruit le
// plancher à partir de ses morceaux (index + une phrase par geste ouvert +
// tous les fermés nommés) — c'est lui qui fait foi. Piège vécu le
// 2026-09-17 : deux mesures successives, 2,2 % puis 1,4 %, toutes deux
// fausses et toutes deux plausibles.
//
// Marge après relèvement : ~20 % sur le plancher, contre 8,0 % avant — de
// quoi absorber plusieurs gestes sans repasser par cette décision.
const CATALOGUE_MAX_CARS = 20700;

/**
 * @param {number} vagueMax — les vagues ouvertes (décision Dimitri, env).
 * @param {{ecran?:string}} [ou] — l'écran courant passe EN PREMIER : c'est là
 *   que Dimitri regarde, donc c'est là que sa demande porte le plus souvent.
 * @returns {string} l'index, borné. Vide si aucune vague n'est ouverte.
 */
function catalogueCompact(vagueMax, ou, budget) {
  const noms = leviersOuverts(vagueMax);
  if (!noms.length) return "";
  const ecran = ou && typeof ou.ecran === "string" ? ou.ecran : null;
  // ⚠️ UN BUDGET PLUS SERRÉ QUE LE PLAFOND, QUAND L'APPELANT DOIT ÉCRIRE APRÈS
  // NOUS. `catalogueAvecDits` ajoute ses avis SOUS cet index : s'il remplit
  // tout, il ne reste pas un caractère pour dire ce qui a été coupé. Le
  // troisième argument est facultatif et ne peut que RESTREINDRE — jamais
  // desserrer le plafond, qui reste le dernier mot.
  const budgetTotal =
    Number.isFinite(budget) && budget > 0
      ? Math.min(budget, CATALOGUE_MAX_CARS)
      : CATALOGUE_MAX_CARS;
  // ⚠️ UN ARGUMENT À VALEURS FERMÉES MONTRE SES VALEURS. Sans ça, le modèle
  // remplit avec du plausible — c'est son travail quand on ne lui dit rien — et
  // le serveur refuse. Vécu le 2026-09-09 : « adresse » au lieu de
  // « adresse_complete », sur un geste qui se serait affiché puis aurait échoué
  // sous le clic de Dimitri. Refuser sans dire ce qu'on attend ferait un
  // opérateur qui échoue poliment, à l'infini.
  const ligne = (n) => {
    const l = LEVIERS[n];
    const vals = l.args_valeurs || {};
    const formes = l.args_formes || {};
    // ══ LES ARGUMENTS RÉSOLUS SORTENT DE LA LIGNE (ADR-27x, lot 0) ═════════
    //
    // Un identifiant que Dimitri ne prononce jamais n'a rien à faire dans la
    // liste qu'on montre au modèle : l'y laisser, c'est l'inviter à le
    // composer — et un identifiant composé est indistinguable d'un identifiant
    // halluciné. Il est donc REMPLACÉ par un jeton court (`vise=logement`), à
    // la place exacte qu'il occupait, et l'explication de `vise` s'écrit UNE
    // FOIS en région 1 du prompt (`lib/operateur-conversation.js`).
    //
    // ⚠️ REMPLACÉ, PAS AJOUTÉ — c'est ce qui rend le delta de budget ≈ 0. La
    // marge du catalogue se compte en centaines de caractères
    // (`src/lib/operateur-catalogue-marge.test.js`) : une phrase par levier
    // l'aurait mangée. Et si DEUX arguments d'un même levier sont résolus, le
    // jeton les fusionne (`vise=logement+quand`) — une seule fois.
    // ⚠️ ET IL RESTE DANS `args_autorises` : seule la LIGNE change. Retirer la
    // clé de la liste fermée ferait refuser la carte au clic.
    const resolus = l.args_resolus || {};
    // ⚠️ ET LA CLÉ RÉSOLUE PEUT ÊTRE POINTÉE (lot 4) : `achat.csId` vit DANS
    // la forme composée `achat`. La feuille sort de la description de la
    // forme (le modèle ne doit pas la composer) et le jeton `vise=…` s'écrit
    // à côté — une fois, comme pour une clé à plat.
    // ⚠️ ET UNE FEUILLE SAISIE SUR LA CARTE SORT AUSSI DE LA LIGNE (revue
    // #1501, M4) : le prix payé ne se compose pas — le modèle ne doit pas le
    // voir, sinon il l'écrit.
    const saisis = l.args_saisis || {};
    const feuillesResolues = (a) =>
      [...Object.keys(resolus), ...Object.keys(saisis)]
        .filter((k) => k.startsWith(`${a}.`))
        .map((k) => k.slice(a.length + 1));
    let viseEcrit = false;
    const jetonVise = () => {
      const reperes = [];
      for (const cle of l.args_autorises)
        for (const k of Object.keys(resolus))
          if (k === cle || k.startsWith(`${cle}.`))
            for (const r of (resolus[k] && resolus[k].par) || [])
              if (!reperes.includes(r)) reperes.push(r);
      return `vise=${reperes.join("+")}`;
    };
    const args = l.args_autorises.length
      ? l.args_autorises
          .map((a) => {
            // Tapé sur la carte, jamais composé — SAUF une saisie
            // conditionnelle (lot 6) : hors de sa condition, l'argument reste
            // composé par le modèle (le libellé d'une ligne de compta), et
            // le lui cacher l'empêcherait de l'écrire. Ce qu'il compose sous
            // la condition est retiré et dit (`reperes-geste.js`).
            if (saisis[a] && !saisis[a].si) return null;
            if (resolus[a]) {
              if (viseEcrit) return null;
              viseEcrit = true;
              return jetonVise();
            }
            if (vals[a]) return `${a}=${vals[a].join("|")}`;
            const f = formes[a];
            if (f) {
              // Trois natures, trois écritures — le modèle doit voir la FORME
              // attendue, pas seulement le nom : un tableau et un objet ne se
              // composent pas de la même façon.
              if (f.liste === true) return `${a}=[id,…]`;
              // ⚠️ UNE CLÉ OBLIGATOIRE SE MARQUE « ! », UNE CLÉ À VALEURS
              // FERMÉES MONTRE SES VALEURS (revue adverse #1449). Sans ça,
              // « unite » — la clé qui dit si « 6 » compte des rouleaux ou des
              // sous-paquets de 6 — était invisible au modèle : il la
              // composait au hasard ou pas du tout, et l'euro écrit variait
              // d'un facteur 6 à 48.
              const req = new Set(f.cles_requises || []);
              const closes = f.cles_valeurs || {};
              const masquees = feuillesResolues(a);
              const decrites = f.cles
                .filter((k) => !masquees.includes(k))
                .map(
                  (k) =>
                    `${k}${req.has(k) ? "!" : ""}${closes[k] ? `=${closes[k].join("|")}` : ""}`,
                )
                .join(",");
              const forme =
                f.texte === true
                  ? `${a}={${decrites}}=texte`
                  : `${a}={${decrites}}=${f.valeurs.join("|")}`;
              if (masquees.length && !viseEcrit) {
                viseEcrit = true;
                return `${forme},${jetonVise()}`;
              }
              return forme;
            }
            return a;
          })
          .filter(Boolean)
          .join(",")
      : "—";
    return `${n}(${args}) — ${l.titre}`;
  };
  // L'écran courant d'abord, le reste ensuite : si la coupe tombe, elle tombe
  // sur ce qui est le plus loin de ce que Dimitri regarde.
  const ici = noms.filter((n) => ecran && LEVIERS[n].ecran === ecran).sort();
  const ailleurs = noms.filter((n) => !ici.includes(n)).sort();
  // ⚠️ UNE COUPE SE DIT — ET SA PHRASE SE RÉSERVE D'AVANCE. Un catalogue
  // tronqué en silence ferait croire au modèle qu'il connaît tous les gestes,
  // et il refuserait avec aplomb une demande légitime. Mais la phrase qui le
  // dit était jusqu'ici ajoutée APRÈS la vérification du budget : l'index
  // pouvait donc dépasser son propre plafond des cent soixante caractères de
  // l'aveu. Mesuré le 2026-09-16 sur un registre gonflé : 18 312 caractères
  // pour un plafond de 18 000. Invisible avec le registre réel (11 807 au
  // pire), et ce sont exactement les défauts qu'on ne voit qu'une fois trop
  // tard. `catalogueAvecDits` réservait déjà la sienne — celle-ci ne l'était
  // pas.
  const AVIS_HORS_ECRAN = `(N autres gestes existent hors de cet écran — si la demande n'est dans aucun de ceux listés, DIS que tu vas devoir chercher, ne réponds pas que c'est impossible.)`;
  const plafond = budgetTotal - AVIS_HORS_ECRAN.length - 8;
  const lignes = [];
  let taille = 0;
  let coupe = 0;
  for (const n of [...ici, ...ailleurs]) {
    const t = ligne(n);
    if (taille + t.length + 1 > plafond) {
      coupe += 1;
      continue;
    }
    lignes.push(t);
    taille += t.length + 1;
  }
  if (coupe)
    lignes.push(
      `(${accord(coupe, "autre geste", { verbe: "existe" })} hors de cet écran — si la demande n'est dans aucun de ceux listés, DIS que tu vas devoir chercher, ne réponds pas que c'est impossible.)`,
    );
  return lignes.join("\n");
}

// ══ LE CATALOGUE AVEC LES PHRASES DE DIMITRI (2026-09-15) ══════════════════
//
// Dimitri, après le premier « oui » réel en prod : « j'ai dû écrire "Où en est
// la réconciliation Beds24 ? Prépare-moi le geste pour regarder" pour qu'il
// comprenne. Personne ne parle comme ça. »
//
// ⚠️ LE DÉFAUT EST DANS LE CATALOGUE, PAS DANS LE MODÈLE. `catalogueCompact`
// ne lui donne qu'un nom d'action et un titre écrit par un développeur —
// `beds24_reconciliation_status(—) — Comparer les données FBZ avec celles de
// Beds24`. Rien là-dedans ne ressemble à « est-ce que tout est synchro ? ».
// Le modèle ne devine pas : il fait correspondre ce qu'on lui montre.
//
// ⚠️ C'EST ELLE QUE LE PROMPT EMPLOIE (`lib/operateur-conversation.js`, depuis
// le 2026-09-15). `catalogueCompact` vit toujours à côté, inchangée : elle est
// la base que celle-ci enrichit, et les tests la comparent ligne pour ligne —
// on AJOUTE sous chaque geste, on ne réécrit jamais sa ligne.
//
// Le format : la ligne du catalogue, suivie des phrases entre chevrons.
//
//   beds24_reconciliation_status(—) — Comparer les données FBZ avec celles de Beds24
//     « Vérifie Beds24 » · « Est-ce que tout est synchro ? »
//
// Borné à DITS_AU_CATALOGUE (3) par levier — mais TROIS EST UN PLAFOND, PAS UNE
// PROMESSE : dans la configuration ouverte aujourd'hui (vagues 1, 2, 3 et 9),
// le budget n'en laisse tenir qu'UNE par geste, et c'est la plus courte qui
// est servie (voir `parCout` plus bas). Écrire « trois phrases par geste »
// quelque part serait décrire un cas qui n'arrive qu'aux deux premières
// vagues.
function catalogueAvecDits(vagueMax, ou) {
  // ⚠️ LES DEUX AVIS SE DÉCLARENT EN PREMIER PARCE QU'ILS SE RÉSERVENT. Ils
  // s'écrivent SOUS l'index ; si l'index a le droit de remplir le plafond, il
  // ne reste rien pour dire ce qui manque — et un manque tu se lit comme
  // une absence. On ampute donc le budget de l'index de ce qu'on devra écrire
  // après lui, avant même de le demander.
  // ⚠️ ACCORDÉS, ET RÉSERVÉS AU PIRE CAS. Les deux avis sont des FONCTIONS
  // (« 1 geste est listé » / « 2 gestes sont listés ») depuis que les nombres
  // s'accordent — mais une réserve doit être calculée sur la forme la PLUS
  // LONGUE, sinon elle sous-estime et la coupe revient. D'où `avis*(99)`.
  const avisCoupe = (n) =>
    `(${accord(n, "geste", { verbe: "est" })} ${nomAccorde(n, "listé")} sans les phrases habituelles de Dimitri — s'il emploie des mots que tu ne retrouves nulle part, cherche parmi eux plutôt que de répondre que c'est impossible.)`;
  const avisFermes = (n) =>
    `(et ${accord(n, "autre geste", { verbe: "existe" })}, pas encore ${nomAccorde(n, "ouvert")}.)`;
  const base = catalogueCompact(
    vagueMax,
    ou,
    CATALOGUE_MAX_CARS - avisCoupe(99).length - avisFermes(99).length - 8,
  );
  if (!base) return "";
  const lignes = base.split("\n");
  // La ligne d'un levier commence par son nom suivi d'une parenthèse ; la
  // ligne de coupe, elle, commence par « ( » — elle ne reçoit rien.
  const entrees = lignes.map((ligne) => {
    const m = /^([a-z][a-z0-9_]*)\(/.exec(ligne);
    const nom = m && LEVIERS[m[1]] ? m[1] : null;
    const a =
      nom &&
      Array.isArray(LEVIERS[nom].dit_comme) &&
      LEVIERS[nom].dit_comme.length;
    return { ligne, nom: a ? nom : null };
  });
  const ecran = ou && typeof ou.ecran === "string" ? ou.ecran : null;

  // ══ QUI EST SERVI EN PREMIER ══════════════════════════════════════════
  //
  // ⚠️ LA PREMIÈRE VERSION SUIVAIT L'ORDRE DU CATALOGUE — écran courant puis
  // ALPHABÉTIQUE — et la revue du 2026-09-15 l'a mesurée : toutes vagues
  // ouvertes, 65 leviers sur 90 perdaient leurs phrases, et LES DIX GESTES
  // D'ARGENT étaient muets sur TOUS les écrans (`pricing_set_manual`,
  // `logement_commission_set`, `site_booking_decision`…). Le commentaire
  // disait « la coupe tombe loin de ce que Dimitri regarde » : c'était faux,
  // elle tombait sur les gestes qui coûtent. Une coupe qui suit l'alphabet ne
  // suit rien du tout.
  //
  // Trois rangs, et le premier est le seul qui compte vraiment :
  //   0 — ce qui touche l'argent ou sort de FBZ, ET les gestes de la vague 1
  //       (ceux que Dimitri emploie depuis le début — « Vérifie Beds24 » en
  //       est) : ce sont ceux qu'il doit pouvoir nommer avec SES mots ;
  //   1 — l'écran où il se trouve ;
  //   2 — le reste.
  const rang = (nom) => {
    const l = LEVIERS[nom];
    if ((l.marquages || []).length) return 0;
    if (l.vague === 1) return 0;
    if (ecran && l.ecran === ecran) return 1;
    return 2;
  };
  const ordre = entrees
    .filter((e) => e.nom)
    .map((e, i) => ({ nom: e.nom, i }))
    .sort((a, b) => rang(a.nom) - rang(b.nom) || a.i - b.i)
    .map((e) => e.nom);

  // ══ LAQUELLE SURVIT QUAND IL N'EN RESTE QU'UNE ═══════════════════════
  //
  // ⚠️ LA PLUS COURTE, ET C'EST UNE CORRECTION MESURÉE (revue du 2026-09-15).
  // La première version prenait les phrases dans l'ordre d'écriture
  // (`slice(0, k)`). Or la dégradation tombe à UNE phrase dès la configuration
  // de production : c'est donc la PREMIÈRE écrite qui survivait — pour la
  // réconciliation, « Est-ce que mes calendriers sont à jour ? ». « Vérifie
  // Beds24 », la phrase que Dimitri a dite et que le registre garde au mot
  // près, n'atteignait jamais le modèle en prod. Le test qui la verrouillait
  // portait sur le REGISTRE, pas sur ce qui part.
  //
  // La plus courte est aussi la moins chère (on en sert plus pour le même
  // budget) et la plus distinctive (« Vérifie Beds24 » désigne un geste,
  // « Est-ce que tout est synchro ? » demande un état). À longueur égale,
  // l'ordre d'écriture tranche — la sortie reste déterministe.
  const parCout = (nom) =>
    LEVIERS[nom].dit_comme
      .map((p, i) => ({ p, i }))
      .sort((a, b) => a.p.length - b.p.length || a.i - b.i)
      .map((e) => e.p);

  const phrase = (nom, k) =>
    "  " +
    parCout(nom)
      .slice(0, k)
      .map((p) => `« ${p} »`)
      .join(" · ");

  // La phrase de coupe compte DANS le budget, pas après : la réserver d'avance
  // est la seule façon que le total ne dépasse jamais, coupe comprise.
  // Réservation au PIRE cas d'accord (pluriel), pour ne jamais sous-estimer.
  const plafond = CATALOGUE_MAX_CARS - avisCoupe(99).length - 4;

  // ══ ET LE BLOC DES FERMÉS SE CHIFFRE AVANT LES PHRASES ════════════════
  //
  // ⚠️ MESURE DU 2026-09-16, ET ELLE RENVERSE L'INTUITION. Les phrases se
  // servaient jusqu'à épuiser le budget AVANT que le bloc « pas encore
  // ouverts » ne s'écrive : elles le mangeaient. Relever le plafond ne
  // réparait donc rien — ça AGGRAVAIT, parce que le budget en plus finançait
  // une DEUXIÈME phrase par geste ouvert au lieu de nommer les fermés.
  // Mesuré à vagues égales ({1,2,3,9}, écran calendrier) :
  //
  //   plafond 15 000 → 20 fermés sur 21 nommés, « Modifier un prix » en est ;
  //   plafond 16 000 →  2 sur 21, et « Modifier un prix » EN SORT ;
  //   plafond 17 000 →  9 sur 21, toujours sans « Modifier un prix ».
  //
  // Autrement dit : le geste dont l'absence a produit le défaut de prod du
  // 2026-09-15 (« baisse le prix de Cocon à 40 € » → « aucun de mes leviers
  // ne touche aux tarifs ») redisparaissait, et cette fois POUR AVOIR DONNÉ
  // PLUS DE BUDGET. Un plafond ne se relève pas sans regarder qui dépense.
  //
  // D'où l'ordre de priorité, du plus dû au moins dû :
  //   1. la ligne de chaque geste ouvert — déjà garantie par catalogueCompact ;
  //   2. UNE phrase par geste ouvert — « Vérifie Beds24 » doit partir ;
  //   3. CHAQUE geste fermé nommé en français avec sa vague ;
  //   4. la deuxième et la troisième phrase — le confort, en dernier.
  //
  // Le coût du bloc est donc calculé ICI, et retiré du budget AVANT qu'on
  // décide de servir une deuxième phrase. Il n'est PAS retiré du budget de la
  // première : on ne rend jamais muet un geste qu'on peut faire pour nommer
  // un geste qu'on ne peut que citer.
  const ouverts = new Set(leviersOuverts(vagueMax));
  const fermes = Object.keys(LEVIERS)
    .filter((n) => !ouverts.has(n))
    .sort((x, y) => LEVIERS[x].vague - LEVIERS[y].vague || (x < y ? -1 : 1));
  // ══ ON NE CITE JAMAIS UN NUMÉRO DE VAGUE À DIMITRI ════════════════════
  //
  // ⚠️ DÉFAUT MESURÉ EN PROD LE 23/09. « Retire la ligne Expert-comptable » a
  // rendu : « ce geste … n'est pas encore ouvert de mon côté (vague 4) ». Or la
  // vague 4 EST ouverte — LEVIER PAR LEVIER (`OPERATEUR_GESTES_VAGUE` nomme
  // ceux qui le sont), et `compta_ligne_retirer` n'y était pas. La phrase
  // désignait donc un ENSEMBLE fermé qui ne l'est pas : Dimitri serait allé
  // chercher au mauvais endroit — « pourquoi la vague 4 est-elle refermée ? »
  // au lieu de « ce geste-là n'est pas encore ouvert ».
  //
  // Et le cas d'une vague ENTIÈREMENT fermée ne rouvre pas la citation, pour
  // trois raisons qui tiennent ensemble : (1) « entièrement fermée » n'est vrai
  // qu'à l'instant où la phrase s'écrit — un seul nom ajouté à la variable la
  // rend fausse, et rien ne relit la phrase ; (2) un numéro de vague est du
  // vocabulaire interne : il ne dit pas où aller, alors que la porte vers
  // l'écran, elle, est déjà dans la phrase ; (3) une règle « cite le numéro
  // SEULEMENT si toute la vague est fermée » demanderait au modèle un calcul
  // qu'il ne peut pas faire — il ne voit pas l'état des autres leviers.
  //
  // Le numéro reste donc à sa place : dans le TRI (on offre les fermés par
  // vague), et dans les champs machine (`geste_ferme.vague`). Jamais dans une
  // phrase.
  const ENTETE_FERMES =
    "— CES GESTES EXISTENT MAIS NE SONT PAS ENCORE OUVERTS. Tu ne les composes JAMAIS. Si sa demande en vise un, dis que CE GESTE-LÀ existe et n'est pas encore ouvert, nomme-le en français, et propose la porte vers l'écran qui le fait. Tu ne cites JAMAIS de numéro de vague : les gestes s'ouvrent un par un, et un numéro laisserait croire qu'un ensemble entier est fermé. Voici les gestes fermés :";
  const ligneFermee = (n) => {
    const l = LEVIERS[n];
    const p = parCout(n)[0];
    return `  ${l.titre}${p ? ` — « ${p} »` : ""} — pas encore ouvert`;
  };
  // ⚠️ LE MÊME COMPTE QUE CELUI DU BLOC PLUS BAS, AU CARACTÈRE PRÈS (en-tête,
  // avis de coupe, sauts de ligne compris) : une réserve qui compterait autre
  // chose que ce qui s'écrit ensuite serait une réserve fausse — trop courte,
  // elle laisserait la coupe revenir ; trop longue, elle volerait des phrases.
  const RESERVE_FERMES = fermes.length
    ? ENTETE_FERMES.length +
      avisFermes(99).length +
      8 +
      fermes.reduce((somme, n) => somme + ligneFermee(n).length + 1, 0)
    : 0;

  // ══ ON DÉGRADE POUR TOUT LE MONDE, PAS POUR LES DERNIERS ══════════════
  //
  // ⚠️ UNE FALAISE N'EST PAS UNE COUPE. Servir trois phrases aux premiers et
  // zéro aux suivants rend une moitié du registre introuvable pendant que
  // l'autre est servie trois fois. On cherche donc d'abord le nombre de
  // phrases que TOUT LE MONDE peut avoir — trois, puis deux, puis une.
  const coutDe = (k) =>
    base.length +
    ordre.reduce((somme, nom) => somme + phrase(nom, k).length + 1, 0);
  let servis = null;
  // Les phrases de CONFORT (la 2e, la 3e) passent après le bloc des fermés :
  // leur budget est amputé de la réserve.
  for (let k = DITS_AU_CATALOGUE; k >= 2 && !servis; k -= 1) {
    if (coutDe(k) <= plafond - RESERVE_FERMES)
      servis = new Map(ordre.map((nom) => [nom, k]));
  }
  // La PREMIÈRE phrase, elle, garde presque tout le budget — elle passe avant
  // les fermés. Presque : on retient de quoi écrire l'AVIS des fermés, et rien
  // d'autre. C'est le minimum incompressible pour que le bloc se RACCOURCISSE
  // au lieu de DISPARAÎTRE (cf. plus bas, « zéro ligne n'est pas zéro
  // nouvelle ») — cinquante caractères qui empêchent un silence.
  const plafondOuverts =
    plafond - (fermes.length ? avisFermes(99).length + 1 : 0);
  if (!servis && coutDe(1) <= plafondOuverts)
    servis = new Map(ordre.map((nom) => [nom, 1]));
  // Et si même UNE phrase pour chacun ne tient pas, alors seulement on coupe —
  // par rang, donc l'argent et le sortant d'abord.
  if (!servis) {
    servis = new Map();
    let taille = base.length;
    for (const nom of ordre) {
      const p = phrase(nom, 1);
      if (taille + p.length + 1 > plafondOuverts) continue;
      taille += p.length + 1;
      servis.set(nom, 1);
    }
  }

  const sortie = entrees.map((e) =>
    e.nom && servis.has(e.nom)
      ? `${e.ligne}\n${phrase(e.nom, servis.get(e.nom))}`
      : e.ligne,
  );
  const muets = ordre.length - servis.size;
  if (muets) sortie.push(avisCoupe(muets));

  // ══ ET CEUX QUI EXISTENT SANS ÊTRE OUVERTS ═══════════════════════════
  //
  // ⚠️ DÉFAUT VU EN PROD LE 2026-09-15, ET C'EST LE PIRE GENRE : « baisse le
  // prix de Cocon à 40 € » s'est vu répondre « aucun de mes leviers ne touche
  // aux tarifs ». C'est FAUX — `pricing_set_manual` existe, il est seulement
  // FERMÉ. Dimitri : « il doit dire "ce geste n'est pas encore ouvert" — c'est
  // très différent, et ça me dit quoi faire. » Une phrase qui dit « je ne sais
  // pas » là où il faudrait dire « pas encore » fait ouvrir dix-sept leviers à
  // quelqu'un qui croit que rien ne marche.
  //
  // Le modèle ne pouvait pas mieux répondre : on ne lui montrait QUE les
  // ouverts. Il disait fidèlement ce qu'il voyait.
  //
  // ⚠️ DÉRIVÉ À CHAQUE APPEL, JAMAIS FIGÉ. La liste se calcule par différence
  // avec `leviersOuverts(vagueMax)` — le même appel que le catalogue. Dimitri
  // ouvre ses leviers un par un, parfois dans l'heure : une liste « fermée »
  // constante annoncerait comme fermé ce qu'il vient d'ouvrir.
  //
  // ⚠️ UNE LIGNE, ET PAS LES ARGUMENTS. Un geste fermé ne se compose pas : lui
  // montrer sa liste d'arguments l'inviterait à essayer. Titre en français +
  // la phrase de Dimitri la plus courte, et RIEN D'AUTRE — ni le nom d'action
  // (un mot qu'il ne doit jamais redire à Dimitri), ni le numéro de vague
  // (23/09 : il désignait un ensemble fermé, alors qu'une vague s'ouvre levier
  // par levier).
  // (`fermes`, `ENTETE_FERMES`, `avisFermes` et `ligneFermee` sont calculés
  // plus haut : c'est leur coût qui a réservé la place ci-dessus.)
  if (fermes.length) {
    // ⚠️ ET SI ÇA NE TIENT PAS, CE SONT LES FERMÉS QUI TOMBENT — jamais un
    // ouvert, et surtout jamais un ouvert d'argent. Un geste qu'on peut faire
    // vaut plus qu'un geste qu'on ne peut que nommer.
    let taille =
      sortie.join("\n").length +
      ENTETE_FERMES.length +
      avisFermes(fermes.length).length +
      8;
    const lignes = [];
    for (const n of fermes) {
      const t = ligneFermee(n);
      if (taille + t.length + 1 > CATALOGUE_MAX_CARS) continue;
      taille += t.length + 1;
      lignes.push(t);
    }
    if (lignes.length) {
      sortie.push(ENTETE_FERMES, ...lignes);
      const coupes = fermes.length - lignes.length;
      if (coupes) sortie.push(avisFermes(coupes));
    } else if (
      sortie.join("\n").length + avisFermes(fermes.length).length + 1 <=
      CATALOGUE_MAX_CARS
    ) {
      // ⚠️ ZÉRO LIGNE N'EST PAS ZÉRO NOUVELLE. Si RIEN ne tient — le cas
      // mesuré le 2026-09-16 à plafond 15 000, vagues {1,2,3,4,9,10} : un seul
      // geste fermé, et le bloc ENTIER disparaissait, avis compris — alors le
      // modèle ne voyait plus aucune trace des gestes fermés et retombait
      // exactement dans le défaut de prod qu'ils sont là pour corriger :
      // « je ne sais pas faire » au lieu de « pas encore ouvert ». Un silence
      // vaut ici un mensonge. L'avis seul coûte cinquante caractères et
      // suffit à ce que le modèle dise « pas encore » plutôt que « jamais ».
      sortie.push(avisFermes(fermes.length));
    }
  }
  return sortie.join("\n");
}

module.exports = {
  LEVIERS,
  catalogueCompact,
  catalogueAvecDits,
  CATALOGUE_MAX_CARS,
  INTERDITS_ABSOLUS,
  ECARTES,
  EXCLUSIONS_ECART,
  messageEcarte,
  verifierEcartes,
  VAGUES,
  lireLevier,
  leviersOuverts,
  filtrerArgs,
  manqueIndispensable,
  ARGS_LIBELLES,
  lireChemin,
  poserChemin,
  retirerChemin,
  saisieSApplique,
  NOMS_BOOLEENS,
  BOOLEENS_LIBELLES,
  motsDuBooleen,
};
